import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Header from '../components/Header';
import { ConcentricReviewWheels } from '../components/ReviewCycleWidget';
import { PurposeIcon, FlowIcon, OutcomeIcon, TipsIcon } from '../components/HowToIcons';
import { API_BASE } from '../config';
import { track } from '../utils/analytics';
import { mergeSectionPreservingMeta, countDeepDiveSections } from '../utils/mergeCanvasUpload';

const DELIVERY_API = `${API_BASE}/delivery`;
const GURU_COLOUR = '#D97706';
const DELIVERY_TAGLINE = 'Turn your direction & design into delivery';
const BANNER_DISMISS_KEY = 'guruone_delivery_banner_dismissed';
const ORGROLES_SEEN_KEY = 'guruone_delivery_orgroles_seen';

// ── Standard initiative categories ──────────────────────────────
// defaultDrafts are pre-written objective drafts that any founder can
// commit, edit or dismiss. They are intentionally incomplete (no
// numbers, dates or owners) so the user has meaningful editing to do
// before committing.
const CATEGORY_CONFIG = {
  revenue: {
    label: 'Revenue performance',
    icon: '📈',
    colour: '#1D9E75',
    description: 'Grow, protect or manage revenue targets',
    defaultDrafts: [
      { id: 'revenue-0', title: 'Increase average deal value', description: 'Identify the top 20% of customers by revenue and define what a premium tier offering would look like for them.' },
      { id: 'revenue-1', title: 'Improve conversion rate from proposal to close', description: 'Review the last 10 proposals and identify the most common reasons for not proceeding.' },
      { id: 'revenue-2', title: 'Develop a recurring revenue stream', description: 'Define one existing service that could be repackaged as a retainer or subscription.' },
    ],
  },
  people: {
    label: 'People & capability',
    icon: '👥',
    colour: '#185FA5',
    description: 'Strategic hires, training, roles and responsibilities',
    defaultDrafts: [
      { id: 'people-0', title: 'Reduce founder dependency in delivery', description: 'Identify the three tasks only the founder currently does and document the process for handing each one over.' },
      { id: 'people-1', title: 'Define role clarity across the team', description: 'Produce a one-page role summary for each team member covering responsibilities, outputs and success measures.' },
      { id: 'people-2', title: 'Build a hiring plan for the next 12 months', description: 'Based on current workload and growth targets, define the next two hires with role, timing and budget.' },
    ],
  },
  customers: {
    label: 'Customer growth',
    icon: '🎯',
    colour: '#7C3AED',
    description: 'Acquisition, retention and satisfaction',
    defaultDrafts: [
      { id: 'customers-0', title: 'Define the ideal customer profile', description: 'Identify the top 5 customers by margin and document what they have in common.' },
      { id: 'customers-1', title: 'Improve customer retention', description: 'Map the current customer journey and identify the point at which customers are most likely to disengage.' },
      { id: 'customers-2', title: 'Build a referral pipeline', description: 'Design a simple referral process and identify 10 existing customers to approach first.' },
    ],
  },
  product: {
    label: 'Product & service development',
    icon: '🚀',
    colour: '#D97706',
    description: 'New offers, improvements and productisation',
    defaultDrafts: [
      { id: 'product-0', title: 'Validate the next service offering', description: 'Speak to 5 existing customers about the problem the new service would solve before committing to building it.' },
      { id: 'product-1', title: 'Improve service delivery consistency', description: 'Document the end-to-end delivery process for the core service and identify the steps most prone to variation.' },
      { id: 'product-2', title: 'Reduce time to deliver', description: 'Map the current delivery timeline and identify the biggest bottleneck slowing it down.' },
    ],
  },
  operations: {
    label: 'Operational efficiency',
    icon: '⚙️',
    colour: '#0891B2',
    description: 'Process, technology, cost and ways of working',
    defaultDrafts: [
      { id: 'operations-0', title: 'Systematise a key repeatable process', description: 'Choose the process that takes the most time per week and document it well enough for someone else to run it.' },
      { id: 'operations-1', title: 'Reduce time spent on administration', description: 'Audit where the team spends time on non-billable admin and identify tools or changes that would cut it by half.' },
      { id: 'operations-2', title: 'Improve visibility of business performance', description: 'Define 5 metrics that would tell you at a glance whether the business is on track and build a simple weekly dashboard.' },
    ],
  },
  financial: {
    label: 'Financial health',
    icon: '💰',
    colour: '#BA7517',
    description: 'Runway, margin, cost control and financial planning',
    defaultDrafts: [
      { id: 'financial-0', title: 'Improve cash flow predictability', description: 'Map the timing of all inflows and outflows over the next 90 days and identify the highest-risk gap.' },
      { id: 'financial-1', title: 'Review pricing against market and margin', description: 'Compare current pricing to 3 competitors and calculate the gross margin on your top 3 services.' },
      { id: 'financial-2', title: 'Reduce the largest controllable cost', description: 'Identify the top 3 costs by size, assess which is most reducible without affecting quality, and model the impact of a 20% reduction.' },
    ],
  },
};

const CATEGORY_KEYS = Object.keys(CATEGORY_CONFIG);

const DRIVER_OPTIONS = [
  'Strategic priority from my direction canvas',
  'Design Guru future state gap',
  'Decision Guru recommendation',
  'Customer or market pressure',
  'Operational problem',
  'Financial pressure',
  'Opportunity I want to capture',
  'Other',
];

const STATUS_OPTIONS = [
  { value: 'not_started', label: 'Not started yet', colour: '#9CA3AF' },
  { value: 'on_track', label: 'On track', colour: '#1D9E75' },
  { value: 'at_risk', label: 'At risk', colour: '#D97706' },
  { value: 'stalled', label: 'Stalled or blocked', colour: '#DC2626' },
  { value: 'done', label: 'Done', colour: '#059669' },
];

// Tile shows the four ongoing-tracking statuses; "Not started yet" is only
// available during intake on the Add Initiative form.
const TILE_STATUS_OPTIONS = STATUS_OPTIONS.filter(s => s.value !== 'not_started');

const STALE_DAYS = 90;     // 3 months
const DUE_SOON_DAYS = 30;  // 1 month

// ── Canvas readiness ─────────────────────────────────────────────
//
// The 8 sections that drive the readiness check are intentionally split
// 4 + 4 across Direction Guru and Design Guru. Picked so that an LLM
// asked to recommend phased delivery initiatives has both a strategic
// shape (where, who, what, what is blocking) and an operational reality
// (team, money, process, customer touchpoints).
//
// Direction Guru (4):
//   • vision               — where the founder wants to take the business
//   • customers            — who the business serves today
//   • value_proposition    — what the business offers them uniquely
//   • current_challenges   — what is actively blocking progress
//
// Design Guru (4):
//   • people_roles            — team capacity and shape
//   • finance_commercial      — financial reality the plan has to live with
//   • process_ways_of_work    — how things actually get done day-to-day
//   • customer_interactions   — how customers experience the business
//
// Other candidates considered but dropped to keep the set at 8:
//   differentiators (overlaps with value_proposition for prioritising),
//   governance_controls (KPI focus — useful but redundant with process
//   for plan-shaping), apps_technology / suppliers_partners /
//   location_infrastructure (too narrow / context-specific to be a
//   universal readiness signal). Icons mirror the per-section icons in
//   StrategyCanvas.js / OperatingModelCanvas.js so the readiness rows
//   read the same visual language as the canvases themselves.
const DIRECTION_CHECKS = [
  { key: 'vision',             label: 'Vision',             icon: '◉', route: '/strategy' },
  { key: 'customers',          label: 'Customers',          icon: '◕', route: '/strategy' },
  { key: 'value_proposition',  label: 'Value proposition',  icon: '◆', route: '/strategy' },
  { key: 'current_challenges', label: 'Current challenges', icon: '⚡', route: '/strategy' },
];

const DESIGN_CHECKS = [
  { key: 'people_roles',          label: 'People, roles & structure', icon: '👥', route: '/operating-model' },
  { key: 'finance_commercial',    label: 'Finance & commercial',      icon: '£', route: '/operating-model' },
  { key: 'process_ways_of_work',  label: 'Process & ways of work',    icon: '⚙', route: '/operating-model' },
  { key: 'customer_interactions', label: 'Customer interactions',     icon: '💬', route: '/operating-model' },
];

function isSectionFilled(data, key) {
  const val = data?.[key];
  if (!val) return false;
  if (typeof val === 'string') return val.trim().length > 3;
  if (Array.isArray(val)) return val.length > 0;
  if (typeof val === 'object') return Object.keys(val).length > 0;
  return false;
}

function calcCanvasPct(data, checks) {
  if (!checks?.length) return 0;
  const filled = checks.filter(c => isSectionFilled(data, c.key)).length;
  return (filled / checks.length) * 100;
}

function assessReadiness(strategyData, designData) {
  const sc = DIRECTION_CHECKS.map(c => ({ ...c, canvas: 'direction', filled: isSectionFilled(strategyData, c.key) }));
  const bc = DESIGN_CHECKS.map(c => ({ ...c, canvas: 'design', filled: isSectionFilled(designData, c.key) }));
  const all = [...sc, ...bc];
  const filled = all.filter(c => c.filled).length;
  return {
    score: filled / all.length,
    directionChecks: sc,
    designChecks: bc,
    gaps: all.filter(c => !c.filled),
    filledCount: filled,
    totalCount: all.length,
  };
}

function extractOrgRoles(designData) {
  const pr = designData?.people_roles;
  if (!pr) return ['Founder / MD'];
  const roles = [pr.founder_title || 'Founder / MD'];
  ['leadership', 'management', 'team'].forEach(layer => {
    if (pr[layer] && typeof pr[layer] === 'object') {
      Object.values(pr[layer]).forEach(arr => {
        if (Array.isArray(arr)) roles.push(...arr);
      });
    }
  });
  if (Array.isArray(pr.advisors)) roles.push(...pr.advisors);
  return [...new Set(roles)].filter(Boolean);
}

function formatTargetDate(iso) {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return '';
    const day = d.getDate();
    const month = d.toLocaleString('en-GB', { month: 'short' });
    const year = d.getFullYear();
    return `${day} ${month} ${year}`;
  } catch {
    return '';
  }
}

// ── Shared sub-components ────────────────────────────────────────
function PhaseBadge({ phase }) {
  const map = {
    1: { label: 'Now', bg: '#FEF3C7', color: '#92400E' },
    2: { label: 'Next', bg: '#DBEAFE', color: '#1E40AF' },
    3: { label: 'Later', bg: '#F3F4F6', color: '#374151' },
  };
  const s = map[phase] || map[3];
  return (
    <span style={{ fontSize: '0.65rem', fontWeight: 700, padding: '2px 8px', borderRadius: 20, background: s.bg, color: s.color, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
      {s.label}
    </span>
  );
}

function ValueChip({ label, value }) {
  const colour = value === 'high' ? '#1D9E75' : value === 'medium' ? '#D97706' : '#9CA3AF';
  return (
    <span style={{ fontSize: '0.65rem', fontWeight: 600, padding: '2px 8px', borderRadius: 20, background: colour + '18', color: colour, border: `1px solid ${colour}33` }}>
      {label}: {value}
    </span>
  );
}

function ReadinessBar({ score }) {
  const pct = Math.round(score * 100);
  const colour = pct >= 70 ? '#1D9E75' : pct >= 40 ? '#D97706' : '#DC2626';
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
      <div style={{ flex: 1, height: 6, borderRadius: 3, background: '#f0f0f0', overflow: 'hidden' }}>
        <div style={{ width: `${pct}%`, height: '100%', background: colour, borderRadius: 3, transition: 'width 0.4s' }} />
      </div>
      <span style={{ fontSize: '0.72rem', fontWeight: 700, color: colour, minWidth: 32 }}>{pct}%</span>
    </div>
  );
}

function CheckRow({ item, onGoThere }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '0.85rem', padding: '0.7rem 0', borderBottom: '1px solid #f5f5f5' }}>
      <span style={{ fontSize: '1.05rem', flexShrink: 0, width: 22, textAlign: 'center', color: item.filled ? '#1D9E75' : '#888' }} aria-hidden="true">
        {item.icon}
      </span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: '0.85rem', fontWeight: 600, color: '#1a1a2e' }}>
          {item.label}
        </div>
      </div>
      {item.filled ? (
        <span style={{ fontSize: '0.7rem', fontWeight: 700, color: '#1D9E75', whiteSpace: 'nowrap', flexShrink: 0 }}>
          ✓ Filled
        </span>
      ) : (
        <button
          onClick={() => onGoThere(item)}
          style={{
            padding: '0.4rem 0.85rem',
            borderRadius: 8,
            background: 'white',
            color: GURU_COLOUR,
            border: `1.5px solid ${GURU_COLOUR}`,
            fontSize: '0.74rem',
            fontWeight: 700,
            cursor: 'pointer',
            fontFamily: 'inherit',
            whiteSpace: 'nowrap',
            flexShrink: 0,
          }}
        >
          Go there now →
        </button>
      )}
    </div>
  );
}

function SmartBreakdown({ obj }) {
  const rows = [
    { label: 'Specific', value: obj.specific },
    { label: 'Metric', value: obj.metric },
    { label: 'Target', value: obj.target },
    { label: 'Time-bound', value: obj.timebound },
    { label: 'Owner', value: obj.owner_role },
    { label: 'Contributors', value: Array.isArray(obj.contributors) ? obj.contributors.join(', ') : obj.contributors },
  ].filter(r => r.value);
  return (
    <div style={{ marginTop: '0.75rem', background: '#f8f9fc', borderRadius: 8, padding: '0.75rem 1rem' }}>
      {rows.map((r, i) => (
        <div key={i} style={{ display: 'flex', gap: '0.75rem', marginBottom: i < rows.length - 1 ? '0.4rem' : 0 }}>
          <span style={{ fontSize: '0.7rem', fontWeight: 700, color: '#aaa', minWidth: 80, textTransform: 'uppercase', letterSpacing: '0.05em', paddingTop: 1 }}>{r.label}</span>
          <span style={{ fontSize: '0.78rem', color: '#333', flex: 1 }}>{r.value}</span>
        </div>
      ))}
    </div>
  );
}

function ObjectiveCard({ obj, objIndex, initiativeIndex, onAccept, onStartRefine, accepted }) {
  const [showBreakdown, setShowBreakdown] = useState(false);
  return (
    <div style={{ background: 'white', borderRadius: 10, border: accepted ? '1.5px solid #1D9E75' : '1px solid #e8e8f0', padding: '1rem 1.1rem', marginBottom: '0.75rem' }}>
      <div style={{ flex: 1 }}>
        {accepted && (
          <span style={{ fontSize: '0.65rem', fontWeight: 700, color: '#1D9E75', textTransform: 'uppercase', letterSpacing: '0.06em', display: 'block', marginBottom: '0.3rem' }}>✓ Saved</span>
        )}
        <p style={{ fontSize: '0.85rem', color: '#1a1a2e', lineHeight: 1.55, margin: 0, fontWeight: 500 }}>{obj.statement}</p>
        {obj.rationale && (
          <p style={{ fontSize: '0.72rem', color: '#aaa', margin: '0.4rem 0 0', fontStyle: 'italic' }}>{obj.rationale}</p>
        )}
        <button onClick={() => setShowBreakdown(s => !s)} style={{ background: 'none', border: 'none', color: '#888', fontSize: '0.72rem', cursor: 'pointer', padding: '0.35rem 0 0', fontFamily: 'inherit' }}>
          {showBreakdown ? '▲ Hide SMART breakdown' : '▼ Show SMART breakdown'}
        </button>
        {showBreakdown && <SmartBreakdown obj={obj} />}
      </div>
      {!accepted && (
        <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.75rem' }}>
          <button onClick={() => onAccept(initiativeIndex, objIndex)} style={{ padding: '0.45rem 1rem', borderRadius: 7, background: '#1D9E75', color: 'white', border: 'none', fontSize: '0.78rem', fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}>
            Accept
          </button>
          <button onClick={() => onStartRefine(initiativeIndex, objIndex)} style={{ padding: '0.45rem 1rem', borderRadius: 7, background: 'white', color: '#555', border: '1px solid #e0e0e0', fontSize: '0.78rem', cursor: 'pointer', fontFamily: 'inherit' }}>
            Refine with AI
          </button>
        </div>
      )}
    </div>
  );
}

// ── Initiative tile (shown inside an expanded CategoryCard) ──────
function InitiativeTile({ initiative, orgRoles, onUpdate, catColour }) {
  const navigate = useNavigate();
  const [savingField, setSavingField] = useState(null);
  const editable = !!initiative.id;

  const takeToDecisionGuru = () => {
    try {
      localStorage.setItem('guruone_decision_prefill', JSON.stringify({
        title: initiative.title || '',
        description: initiative.description || '',
      }));
    } catch { /* localStorage failure is non-fatal — proceed without prefill */ }
    navigate('/decision');
  };

  const handleField = async (field, value) => {
    setSavingField(field);
    try {
      await onUpdate(initiative.id, { [field]: value });
    } finally {
      setSavingField(null);
    }
  };

  // Make sure the currently-saved owner is visible in the dropdown even if
  // it has been removed from the org chart — avoids silently losing it.
  const ownerOptions = (() => {
    const set = new Set(orgRoles);
    if (initiative.owner_role) set.add(initiative.owner_role);
    return [...set];
  })();

  const isDone = initiative.status === 'done';
  const todayISO = new Date().toISOString().slice(0, 10);
  const dueSoonCutoff = new Date(Date.now() + DUE_SOON_DAYS * 24 * 3600 * 1000).toISOString().slice(0, 10);
  const isOverdue = !!initiative.target_date && initiative.target_date < todayISO && !isDone;
  const isDueSoon = !isOverdue && !isDone && !!initiative.target_date
    && initiative.target_date >= todayISO && initiative.target_date <= dueSoonCutoff;

  // Stale = no status update in 3+ months (and not done)
  const staleCutoff = Date.now() - STALE_DAYS * 24 * 3600 * 1000;
  const lastUpdateMs = initiative.last_status_update ? new Date(initiative.last_status_update).getTime() : NaN;
  const isStale = !isDone && (!isFinite(lastUpdateMs) || lastUpdateMs < staleCutoff);

  // Accent priority: done > overdue > due-soon > category
  const accentColour = isDone ? '#059669'
    : isOverdue ? '#DC2626'
    : isDueSoon ? '#D97706'
    : catColour;

  const pillStyle = {
    fontSize: '0.7rem',
    padding: '0.32rem 0.5rem',
    border: `1px solid ${catColour}33`,
    borderRadius: 6,
    background: catColour + '0d',
    color: '#3f3f46',
    fontFamily: 'inherit',
    cursor: editable ? 'pointer' : 'not-allowed',
    minWidth: 0,
    maxWidth: '100%',
    boxSizing: 'border-box',
  };

  return (
    <div style={{
      background: 'white',
      borderRadius: 8,
      border: `1px solid ${catColour}22`,
      borderLeft: `4px solid ${accentColour}`,
      padding: '0.7rem 0.85rem',
      display: 'flex',
      flexDirection: 'column',
      gap: '0.4rem',
      minWidth: 0,
      opacity: isDone ? 0.75 : 1,
    }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.5rem', justifyContent: 'space-between' }}>
        <div style={{ fontSize: '0.82rem', fontWeight: 700, color: '#1a1a2e', lineHeight: 1.35, flex: 1, minWidth: 0, wordBreak: 'break-word', textDecoration: isDone ? 'line-through' : 'none' }}>{initiative.title}</div>
        <div style={{ display: 'flex', gap: '0.3rem', alignItems: 'center', flexShrink: 0, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
          {isOverdue && (
            <span style={{ fontSize: '0.6rem', fontWeight: 700, padding: '2px 7px', borderRadius: 20, background: '#FEE2E2', color: '#991B1B', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
              Overdue
            </span>
          )}
          {isDueSoon && (
            <span style={{ fontSize: '0.6rem', fontWeight: 700, padding: '2px 7px', borderRadius: 20, background: '#FEF3C7', color: '#92400E', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
              Due soon
            </span>
          )}
          {isStale && !isOverdue && !isDueSoon && (
            <span title="Status not updated in 3+ months — give it a tap" style={{ fontSize: '0.6rem', fontWeight: 700, padding: '2px 7px', borderRadius: 20, background: '#F3F4F6', color: '#6B7280', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
              Stale
            </span>
          )}
          {initiative.phase && <PhaseBadge phase={initiative.phase} />}
        </div>
      </div>
      {initiative.description && (
        <div style={{ fontSize: '0.72rem', color: '#888', lineHeight: 1.45, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden', wordBreak: 'break-word' }}>{initiative.description}</div>
      )}

      <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap', marginTop: '0.15rem' }}>
        <select
          value={initiative.owner_role || ''}
          onChange={e => editable && handleField('owner_role', e.target.value)}
          disabled={!editable}
          aria-label="Owner role"
          style={pillStyle}
        >
          <option value="">👤 Assign owner…</option>
          {ownerOptions.map(r => <option key={r} value={r}>{r}</option>)}
        </select>
        <input
          type="date"
          value={initiative.target_date || ''}
          onChange={e => editable && handleField('target_date', e.target.value)}
          disabled={!editable}
          aria-label="Target date"
          style={{ ...pillStyle, minWidth: 130 }}
        />
      </div>

      <div style={{ display: 'flex', gap: '0.3rem', flexWrap: 'wrap', marginTop: '0.1rem' }}>
        {TILE_STATUS_OPTIONS.map(opt => {
          const active = initiative.status === opt.value;
          return (
            <button
              key={opt.value}
              onClick={() => editable && handleField('status', opt.value)}
              disabled={!editable}
              style={{
                fontSize: '0.65rem',
                padding: '0.25rem 0.55rem',
                borderRadius: 14,
                border: `1px solid ${active ? opt.colour : '#e5e7eb'}`,
                background: active ? opt.colour + '18' : 'white',
                color: active ? opt.colour : '#999',
                fontWeight: active ? 700 : 500,
                cursor: editable ? 'pointer' : 'not-allowed',
                fontFamily: 'inherit',
                whiteSpace: 'nowrap',
              }}
            >
              {opt.label}
            </button>
          );
        })}
      </div>

      {savingField && <div style={{ fontSize: '0.62rem', color: '#aaa', fontStyle: 'italic' }}>Saving…</div>}
      {!editable && <div style={{ fontSize: '0.62rem', color: '#bbb', fontStyle: 'italic' }}>Owner, date & status editable after save</div>}
      {initiative.target_date && (
        <div style={{ fontSize: '0.62rem', color: accentColour, fontWeight: 600 }}>
          {isDone ? 'Done · target was ' : isOverdue ? 'Was due ' : 'By '}{formatTargetDate(initiative.target_date)}
        </div>
      )}

      <button
        onClick={takeToDecisionGuru}
        style={{
          alignSelf: 'flex-end',
          background: 'none',
          border: 'none',
          padding: '0.15rem 0',
          marginTop: '0.1rem',
          fontFamily: 'inherit',
          fontSize: '0.65rem',
          color: '#9CA3AF',
          cursor: 'pointer',
          width: 'auto',
        }}
      >
        Not sure how to approach this? Take it to Decision Guru →
      </button>
    </div>
  );
}

// ── SMART rating row ─────────────────────────────────────────────
const SMART_DIMS = [
  { key: 'S', word: 'Specific' },
  { key: 'M', word: 'Measurable' },
  { key: 'A', word: 'Achievable' },
  { key: 'R', word: 'Relevant' },
  { key: 'T', word: 'Time-bound' },
];

function smartDotColour(rating) {
  if (rating === 'green') return '#1D9E75';
  if (rating === 'amber') return '#D97706';
  if (rating === 'red') return '#DC2626';
  return '#9CA3AF';
}

function SmartRatingRow({ rating, hasEditedSinceRate, onReRate }) {
  const [expandedDim, setExpandedDim] = useState(null);
  if (!rating) return null;

  return (
    <div style={{ marginTop: '0.4rem' }}>
      <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
        {SMART_DIMS.map(d => {
          const r = rating[d.key]?.rating;
          const active = expandedDim === d.key;
          return (
            <button
              key={d.key}
              onClick={() => setExpandedDim(active ? null : d.key)}
              title={d.word}
              style={{
                background: active ? '#f3f4f6' : 'none',
                border: 'none',
                cursor: 'pointer',
                padding: '2px 6px',
                borderRadius: 6,
                display: 'inline-flex',
                alignItems: 'center',
                gap: 4,
                fontFamily: 'inherit',
                width: 'auto',
              }}
            >
              <span style={{ fontSize: 11, fontWeight: 700, color: '#666' }}>{d.key}</span>
              <span style={{ width: 8, height: 8, borderRadius: '50%', background: smartDotColour(r), display: 'inline-block' }} />
            </button>
          );
        })}
        {hasEditedSinceRate && (
          <button
            onClick={onReRate}
            style={{ background: 'none', border: 'none', color: '#888', fontSize: 11, cursor: 'pointer', padding: 0, fontFamily: 'inherit', textDecoration: 'underline', marginLeft: 4 }}
          >
            Re-rate
          </button>
        )}
      </div>
      {expandedDim && rating[expandedDim]?.explanation && (
        <div style={{ fontSize: 11, color: '#555', marginTop: 4, fontStyle: 'italic', lineHeight: 1.45 }}>
          <strong style={{ color: '#1a1a2e', fontWeight: 700 }}>{SMART_DIMS.find(d => d.key === expandedDim)?.word}: </strong>
          {rating[expandedDim].explanation}
        </div>
      )}
    </div>
  );
}

// ── Improved-text renderer (highlights [bracketed] gaps) ─────────
function ImprovedText({ text }) {
  const parts = (text || '').split(/(\[[^\]]+\])/g);
  return (
    <span>
      {parts.map((part, i) => {
        if (part.startsWith('[') && part.endsWith(']')) {
          return (
            <strong key={i} style={{ color: '#92400E', background: '#FEF3C7', padding: '0 5px', borderRadius: 4, fontWeight: 700 }}>
              {part}
            </strong>
          );
        }
        return <React.Fragment key={i}>{part}</React.Fragment>;
      })}
    </span>
  );
}

// ── Draft objective (in a category card, before commit) ──────────
function DraftObjective({ draft, category, orgRoles, onCommit, onDismiss }) {
  const [expanded, setExpanded] = useState(false);
  const [title, setTitle] = useState(draft.title);
  const [description, setDescription] = useState(draft.description);
  const [owner, setOwner] = useState('');
  const [startDate, setStartDate] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [committing, setCommitting] = useState(false);
  // null | 'loading' | 'all-green' | 'error' | { improved, gaps }
  const [improveState, setImproveState] = useState(null);

  const allFilled = !!(title.trim() && description.trim() && owner && startDate && dueDate);

  const callImprove = async (skipAllGreenCheck) => {
    setImproveState('loading');
    try {
      if (!skipAllGreenCheck) {
        const ratingRes = await axios.post(`${DELIVERY_API}/objectives/0/smart-rate`, { title, description, owner, startDate, dueDate });
        const r = ratingRes.data || {};
        const allGreen = SMART_DIMS.every(d => r[d.key]?.rating === 'green');
        if (allGreen) { setImproveState('all-green'); return; }
      }
      const improveRes = await axios.post(`${DELIVERY_API}/objectives/0/smart-improve`, { title, description, owner, startDate, dueDate });
      setImproveState({ improved: improveRes.data.improved, gaps: improveRes.data.gaps || [] });
    } catch (e) {
      setImproveState('error');
    }
  };

  const acceptImproved = () => {
    if (typeof improveState !== 'object' || !improveState?.improved) return;
    setDescription(improveState.improved);
    setImproveState(null);
  };

  const handleConfirm = async () => {
    if (!allFilled || committing) return;
    setCommitting(true);
    try {
      await onCommit({
        draftId: draft.id,
        title: title.trim(),
        description: description.trim(),
        owner,
        startDate,
        dueDate,
        category,
      });
      track('delivery_initiative_created', { category, source: 'draft' });
    } finally {
      setCommitting(false);
    }
  };

  const containerStyle = {
    background: '#F9FAFB',
    border: '1.5px dashed #d1d5db',
    borderRadius: 10,
    padding: '0.7rem 0.85rem',
    marginBottom: '0.55rem',
    position: 'relative',
  };

  const draftPill = (
    <span style={{ fontSize: '0.6rem', fontWeight: 700, padding: '2px 7px', borderRadius: 20, background: '#e5e7eb', color: '#4b5563', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
      Draft
    </span>
  );

  if (!expanded) {
    return (
      <div style={containerStyle}>
        <div style={{ display: 'flex', gap: '0.6rem', alignItems: 'flex-start' }}>
          <div
            onClick={() => setExpanded(true)}
            style={{ flex: 1, minWidth: 0, cursor: 'pointer' }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: '0.3rem' }}>
              {draftPill}
              <span style={{ fontSize: '0.85rem', fontWeight: 700, color: '#1a1a2e' }}>{title}</span>
            </div>
            <div style={{ fontSize: '0.74rem', color: '#666', lineHeight: 1.45 }}>{description}</div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 4, flexShrink: 0 }}>
            <button
              onClick={() => setExpanded(true)}
              aria-label="Edit draft"
              title="Edit"
              style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '0.85rem', color: '#888', padding: '0.2rem 0.4rem', fontFamily: 'inherit' }}
            >
              ✎
            </button>
            <button
              onClick={() => onDismiss(draft.id)}
              aria-label="Remove draft"
              title="Remove"
              style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '0.95rem', color: '#9ca3af', padding: '0.15rem 0.4rem', lineHeight: 1, fontFamily: 'inherit' }}
            >
              ×
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Expanded — editable fields
  const ownerOptions = (() => {
    const set = new Set(orgRoles);
    if (owner) set.add(owner);
    return [...set];
  })();

  const labelStyle = { fontSize: 11, fontWeight: 700, color: '#666', textTransform: 'uppercase', letterSpacing: '0.06em', display: 'block', marginBottom: '0.25rem' };
  const inputStyle = { width: '100%', padding: '0.45rem 0.65rem', border: '1px solid #e5e7eb', borderRadius: 7, fontSize: '0.78rem', fontFamily: 'inherit', boxSizing: 'border-box', background: 'white', color: '#1a1a2e', outline: 'none' };

  return (
    <div style={containerStyle}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: '0.55rem' }}>
        {draftPill}
        <div style={{ flex: 1, fontSize: '0.7rem', color: '#888' }}>Editing draft</div>
        <button
          onClick={() => onDismiss(draft.id)}
          aria-label="Remove draft"
          title="Remove"
          style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '1rem', color: '#9ca3af', padding: '0.15rem 0.4rem', lineHeight: 1, fontFamily: 'inherit' }}
        >
          ×
        </button>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.55rem' }}>
        <div>
          <label style={labelStyle}>Title</label>
          <input value={title} onChange={e => setTitle(e.target.value)} style={inputStyle} />
        </div>
        <div>
          <label style={labelStyle}>Description</label>
          <textarea value={description} onChange={e => setDescription(e.target.value)} rows={2} style={{ ...inputStyle, resize: 'vertical' }} />
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: '0.5rem' }}>
          <div>
            <label style={labelStyle}>Owner</label>
            <select value={owner} onChange={e => setOwner(e.target.value)} style={inputStyle}>
              <option value="">Select…</option>
              {ownerOptions.map(r => <option key={r} value={r}>{r}</option>)}
            </select>
          </div>
          <div>
            <label style={labelStyle}>Start date</label>
            <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} style={inputStyle} />
          </div>
          <div>
            <label style={labelStyle}>Due date</label>
            <input type="date" value={dueDate} onChange={e => setDueDate(e.target.value)} style={inputStyle} />
          </div>
        </div>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: '0.85rem', marginTop: '0.7rem', flexWrap: 'wrap' }}>
        <button
          onClick={handleConfirm}
          disabled={!allFilled || committing}
          aria-label="Confirm draft"
          title={allFilled ? 'Confirm and save' : 'Fill in all fields to confirm'}
          style={{
            width: 36, height: 36, borderRadius: '50%',
            border: 'none',
            background: !allFilled ? '#e5e7eb' : '#1D9E75',
            color: !allFilled ? '#9ca3af' : 'white',
            fontSize: '1rem', fontWeight: 800,
            cursor: !allFilled || committing ? 'not-allowed' : 'pointer',
            fontFamily: 'inherit',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          ✓
        </button>
        <button
          onClick={() => callImprove(false)}
          disabled={improveState === 'loading'}
          style={{ background: 'none', border: 'none', color: GURU_COLOUR, fontSize: '0.78rem', fontWeight: 600, cursor: improveState === 'loading' ? 'wait' : 'pointer', padding: 0, fontFamily: 'inherit', textDecoration: 'underline' }}
        >
          {improveState === 'loading' ? 'Thinking…' : 'Help me make this more SMART'}
        </button>
      </div>

      {improveState === 'error' && (
        <div style={{ marginTop: '0.6rem', fontSize: '0.74rem', color: '#991B1B' }}>
          Could not generate a suggestion. Try again in a moment.
        </div>
      )}

      {improveState === 'all-green' && (
        <div style={{ marginTop: '0.7rem', background: '#FFFBEB', border: '1px solid #FDE68A', borderLeft: '3px solid #D97706', borderRadius: 8, padding: '0.75rem 0.85rem' }}>
          <div style={{ fontSize: '0.78rem', color: '#1a1a2e', lineHeight: 1.5 }}>
            Your objective is looking strong across all SMART dimensions. Would you like me to suggest an alternative version anyway?
          </div>
          <div style={{ display: 'flex', gap: '0.6rem', marginTop: '0.55rem' }}>
            <button onClick={() => callImprove(true)} style={{ padding: '0.35rem 0.85rem', borderRadius: 7, background: '#D97706', color: 'white', border: 'none', fontSize: '0.74rem', fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>
              Yes, suggest one
            </button>
            <button onClick={() => setImproveState(null)} style={{ background: 'none', border: 'none', color: '#888', fontSize: '0.74rem', cursor: 'pointer', padding: 0, fontFamily: 'inherit' }}>
              No thanks
            </button>
          </div>
        </div>
      )}

      {typeof improveState === 'object' && improveState && improveState.improved && (
        <div style={{ marginTop: '0.7rem', background: '#FFFBEB', border: '1px solid #FDE68A', borderLeft: '3px solid #D97706', borderRadius: 8, padding: '0.75rem 0.85rem' }}>
          <div style={{ fontSize: '0.62rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: '#92400E', marginBottom: '0.4rem' }}>Suggested improvement</div>
          <div style={{ fontSize: '0.82rem', color: '#1a1a2e', lineHeight: 1.55 }}>
            <ImprovedText text={improveState.improved} />
          </div>
          {improveState.gaps?.length > 0 && (
            <ul style={{ margin: '0.55rem 0 0', paddingLeft: '1.1rem', fontSize: '0.72rem', color: '#7B5A35', lineHeight: 1.5 }}>
              {improveState.gaps.map((g, i) => <li key={i}>{g}</li>)}
            </ul>
          )}
          <div style={{ display: 'flex', gap: '0.6rem', marginTop: '0.7rem' }}>
            <button onClick={acceptImproved} style={{ padding: '0.35rem 0.85rem', borderRadius: 7, background: '#1D9E75', color: 'white', border: 'none', fontSize: '0.74rem', fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>
              Accept and edit
            </button>
            <button onClick={() => setImproveState(null)} style={{ background: 'none', border: '1px solid #e0e0e0', color: '#666', fontSize: '0.74rem', padding: '0.35rem 0.85rem', borderRadius: 7, cursor: 'pointer', fontFamily: 'inherit' }}>
              Ignore
            </button>
          </div>
        </div>
      )}

      <div style={{ marginTop: '0.5rem' }}>
        <button onClick={() => setExpanded(false)} style={{ background: 'none', border: 'none', color: '#888', fontSize: '0.7rem', cursor: 'pointer', padding: 0, fontFamily: 'inherit' }}>
          ▲ Collapse
        </button>
      </div>
    </div>
  );
}

// ── Committed-objective block ────────────────────────────────────
// Wraps an InitiativeTile with the SMART rating row, the
// "Help me make this more SMART" affordance, and an inline edit
// mode for title/description that pops open when the user accepts
// a smart-improve suggestion (or clicks the pencil directly).
function CommittedObjectiveBlock({ initiative, ratingRecord, orgRoles, catColour, onUpdate, onReRate }) {
  // null | 'loading' | 'all-green' | 'error' | { improved, gaps }
  const [improveState, setImproveState] = useState(null);
  const [editingText, setEditingText] = useState(false);
  const [editTitle, setEditTitle] = useState(initiative.title || '');
  const [editDescription, setEditDescription] = useState(initiative.description || '');
  const [savingText, setSavingText] = useState(false);

  const editedSinceRate = !!ratingRecord && (
    ratingRecord.ratedFor?.title !== initiative.title ||
    ratingRecord.ratedFor?.description !== initiative.description
  );

  const startTextEdit = (suggestedDescription) => {
    setEditTitle(initiative.title || '');
    setEditDescription(typeof suggestedDescription === 'string' ? suggestedDescription : (initiative.description || ''));
    setEditingText(true);
  };

  const cancelTextEdit = () => {
    setEditingText(false);
  };

  const saveTextEdit = async () => {
    if (savingText) return;
    setSavingText(true);
    try {
      await onUpdate(initiative.id, { title: editTitle.trim(), description: editDescription.trim() });
      setEditingText(false);
    } finally {
      setSavingText(false);
    }
  };

  const callImprove = async (skipAllGreenCheck) => {
    setImproveState('loading');
    try {
      if (!skipAllGreenCheck) {
        const ratingRes = await axios.post(`${DELIVERY_API}/objectives/${initiative.id || 0}/smart-rate`, {
          title: initiative.title,
          description: initiative.description,
          owner: initiative.owner_role,
          startDate: '',
          dueDate: initiative.target_date,
        });
        const r = ratingRes.data || {};
        const allGreen = SMART_DIMS.every(d => r[d.key]?.rating === 'green');
        if (allGreen) { setImproveState('all-green'); return; }
      }
      const improveRes = await axios.post(`${DELIVERY_API}/objectives/${initiative.id || 0}/smart-improve`, {
        title: initiative.title,
        description: initiative.description,
        owner: initiative.owner_role,
        startDate: '',
        dueDate: initiative.target_date,
      });
      setImproveState({ improved: improveRes.data.improved, gaps: improveRes.data.gaps || [] });
    } catch (e) {
      setImproveState('error');
    }
  };

  const acceptImproved = () => {
    if (typeof improveState !== 'object' || !improveState?.improved) return;
    startTextEdit(improveState.improved);
    setImproveState(null);
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.3rem' }}>
      <InitiativeTile initiative={initiative} orgRoles={orgRoles} onUpdate={onUpdate} catColour={catColour} />

      {ratingRecord?.rating && (
        <SmartRatingRow
          rating={ratingRecord.rating}
          hasEditedSinceRate={editedSinceRate}
          onReRate={() => onReRate(initiative)}
        />
      )}

      {!editingText && (
        <div style={{ display: 'flex', gap: '0.85rem', alignItems: 'center', flexWrap: 'wrap' }}>
          <button
            onClick={() => callImprove(false)}
            disabled={improveState === 'loading' || !initiative.id}
            style={{ background: 'none', border: 'none', color: GURU_COLOUR, fontSize: '0.7rem', fontWeight: 600, cursor: improveState === 'loading' ? 'wait' : 'pointer', padding: 0, fontFamily: 'inherit', textDecoration: 'underline' }}
          >
            {improveState === 'loading' ? 'Thinking…' : 'Help me make this more SMART'}
          </button>
          <button
            onClick={() => startTextEdit()}
            disabled={!initiative.id}
            style={{ background: 'none', border: 'none', color: '#888', fontSize: '0.7rem', cursor: 'pointer', padding: 0, fontFamily: 'inherit' }}
            title="Edit title and description"
          >
            ✎ Edit text
          </button>
        </div>
      )}

      {improveState === 'error' && (
        <div style={{ fontSize: '0.7rem', color: '#991B1B' }}>
          Could not generate a suggestion. Try again in a moment.
        </div>
      )}

      {improveState === 'all-green' && (
        <div style={{ background: '#FFFBEB', border: '1px solid #FDE68A', borderLeft: '3px solid #D97706', borderRadius: 8, padding: '0.65rem 0.85rem' }}>
          <div style={{ fontSize: '0.75rem', color: '#1a1a2e', lineHeight: 1.5 }}>
            Your objective is looking strong across all SMART dimensions. Would you like me to suggest an alternative version anyway?
          </div>
          <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.5rem' }}>
            <button onClick={() => callImprove(true)} style={{ padding: '0.3rem 0.75rem', borderRadius: 7, background: '#D97706', color: 'white', border: 'none', fontSize: '0.7rem', fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>
              Yes, suggest one
            </button>
            <button onClick={() => setImproveState(null)} style={{ background: 'none', border: 'none', color: '#888', fontSize: '0.7rem', cursor: 'pointer', padding: 0, fontFamily: 'inherit' }}>
              No thanks
            </button>
          </div>
        </div>
      )}

      {typeof improveState === 'object' && improveState && improveState.improved && (
        <div style={{ background: '#FFFBEB', border: '1px solid #FDE68A', borderLeft: '3px solid #D97706', borderRadius: 8, padding: '0.7rem 0.85rem' }}>
          <div style={{ fontSize: '0.6rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: '#92400E', marginBottom: '0.35rem' }}>Suggested improvement</div>
          <div style={{ fontSize: '0.78rem', color: '#1a1a2e', lineHeight: 1.55 }}>
            <ImprovedText text={improveState.improved} />
          </div>
          {improveState.gaps?.length > 0 && (
            <ul style={{ margin: '0.5rem 0 0', paddingLeft: '1.1rem', fontSize: '0.7rem', color: '#7B5A35', lineHeight: 1.5 }}>
              {improveState.gaps.map((g, i) => <li key={i}>{g}</li>)}
            </ul>
          )}
          <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.6rem' }}>
            <button onClick={acceptImproved} style={{ padding: '0.3rem 0.75rem', borderRadius: 7, background: '#1D9E75', color: 'white', border: 'none', fontSize: '0.7rem', fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>
              Accept and edit
            </button>
            <button onClick={() => setImproveState(null)} style={{ background: 'none', border: '1px solid #e0e0e0', color: '#666', fontSize: '0.7rem', padding: '0.3rem 0.75rem', borderRadius: 7, cursor: 'pointer', fontFamily: 'inherit' }}>
              Ignore
            </button>
          </div>
        </div>
      )}

      {editingText && (
        <div style={{ background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 8, padding: '0.7rem 0.85rem', display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: '#666', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Edit title and description</div>
          <input
            value={editTitle}
            onChange={e => setEditTitle(e.target.value)}
            placeholder="Title"
            style={{ width: '100%', padding: '0.45rem 0.65rem', border: '1px solid #e5e7eb', borderRadius: 7, fontSize: '0.78rem', fontFamily: 'inherit', boxSizing: 'border-box', outline: 'none', background: 'white' }}
          />
          <textarea
            value={editDescription}
            onChange={e => setEditDescription(e.target.value)}
            placeholder="Description"
            rows={3}
            style={{ width: '100%', padding: '0.45rem 0.65rem', border: '1px solid #e5e7eb', borderRadius: 7, fontSize: '0.78rem', fontFamily: 'inherit', resize: 'vertical', boxSizing: 'border-box', outline: 'none', background: 'white' }}
          />
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <button
              onClick={saveTextEdit}
              disabled={savingText || !editTitle.trim()}
              style={{ padding: '0.35rem 0.85rem', borderRadius: 7, background: !editTitle.trim() || savingText ? '#e5e7eb' : '#1D9E75', color: !editTitle.trim() || savingText ? '#9ca3af' : 'white', border: 'none', fontSize: '0.74rem', fontWeight: 700, cursor: !editTitle.trim() || savingText ? 'not-allowed' : 'pointer', fontFamily: 'inherit' }}
            >
              {savingText ? 'Saving…' : 'Save'}
            </button>
            <button
              onClick={cancelTextEdit}
              disabled={savingText}
              style={{ padding: '0.35rem 0.85rem', borderRadius: 7, background: 'white', color: '#555', border: '1px solid #e0e0e0', fontSize: '0.74rem', fontWeight: 600, cursor: savingText ? 'not-allowed' : 'pointer', fontFamily: 'inherit' }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Dashboard visual overview grid ───────────────────────────────
function DashboardOverview({ initiatives }) {
  const total = initiatives.length;
  return (
    <div style={{ marginBottom: '1.5rem' }}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '0.65rem' }}>
        {CATEGORY_KEYS.map(k => {
          const cfg = CATEGORY_CONFIG[k];
          const catInits = initiatives.filter(i => i.category === k);
          const count = catInits.length;
          const hasAny = count > 0;
          return (
            <div key={k} style={{
              background: hasAny ? 'white' : '#fafafa',
              border: `1.5px solid ${hasAny ? cfg.colour + '44' : '#efefef'}`,
              borderRadius: 12,
              padding: '0.9rem 1rem',
              borderTop: `3px solid ${hasAny ? cfg.colour : '#e8e8f0'}`,
              transition: 'all 0.2s',
              position: 'relative',
              overflow: 'hidden',
            }}>
              {/* Background icon watermark */}
              <div style={{ position: 'absolute', right: 10, bottom: 6, fontSize: '2rem', opacity: hasAny ? 0.07 : 0.04, pointerEvents: 'none', lineHeight: 1 }}>
                {cfg.icon}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.55rem' }}>
                <div style={{ width: 28, height: 28, borderRadius: 7, background: hasAny ? cfg.colour + '18' : '#f0f0f0', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.9rem', flexShrink: 0 }}>
                  {cfg.icon}
                </div>
                <span style={{ fontSize: '0.75rem', fontWeight: 700, color: hasAny ? '#1a1a2e' : '#aaa', lineHeight: 1.2 }}>{cfg.label}</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'flex-end', gap: '0.4rem' }}>
                <span style={{ fontSize: '1.5rem', fontWeight: 800, color: hasAny ? cfg.colour : '#ddd', lineHeight: 1 }}>{count}</span>
                <span style={{ fontSize: '0.7rem', color: '#aaa', marginBottom: '0.18rem' }}>
                  {count === 0 ? 'no initiatives' : count === 1 ? 'initiative' : 'initiatives'}
                </span>
              </div>
              {/* Mini bar showing share of total */}
              <div style={{ marginTop: '0.6rem', height: 3, borderRadius: 2, background: '#f0f0f0', overflow: 'hidden' }}>
                <div style={{ height: '100%', width: total > 0 ? `${Math.round((count / total) * 100)}%` : '0%', background: cfg.colour, borderRadius: 2, transition: 'width 0.4s' }} />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── Category Dashboard Card ──────────────────────────────────────
function CategoryCard({ catKey, initiatives, orgRoles, onUpdateInitiative, smartRatings, onCommitDraft, onDismissDraft, onReRate, dismissedDrafts, expanded, onToggle }) {
  const cfg = CATEGORY_CONFIG[catKey];
  const catInitiatives = initiatives.filter(i => i.category === catKey);
  const hasContent = catInitiatives.length > 0;
  const statusColour = hasContent ? cfg.colour : '#e0e0e0';
  const visibleDrafts = (cfg.defaultDrafts || []).filter(d => !dismissedDrafts.includes(d.id));

  return (
    <div style={{
      background: 'white',
      borderRadius: 12,
      border: `1.5px solid ${hasContent ? cfg.colour + '33' : '#f0f0f0'}`,
      overflow: 'hidden',
      gridColumn: expanded ? '1 / -1' : 'auto',
      transition: 'grid-column 0.15s',
    }}>
      <button
        onClick={onToggle}
        style={{ width: '100%', display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '0.9rem 1.1rem', background: 'none', border: 'none', cursor: 'pointer', fontFamily: 'inherit', textAlign: 'left' }}>
        <div style={{ width: 34, height: 34, borderRadius: 8, background: hasContent ? cfg.colour + '15' : '#f7f7f9', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1rem', flexShrink: 0, border: `1px solid ${statusColour}33` }}>
          {cfg.icon}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: '0.88rem', fontWeight: 700, color: '#1a1a2e', marginBottom: '0.1rem' }}>{cfg.label}</div>
          <div style={{ fontSize: '0.72rem', color: '#aaa', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{cfg.description}</div>
        </div>
        {hasContent && (
          <span style={{ fontSize: '0.65rem', fontWeight: 700, padding: '2px 8px', borderRadius: 20, background: cfg.colour + '15', color: cfg.colour, whiteSpace: 'nowrap', marginRight: '0.5rem' }}>
            {catInitiatives.length}
          </span>
        )}
        <span style={{ fontSize: '0.72rem', color: '#aaa', flexShrink: 0 }}>{expanded ? '▲' : '▼'}</span>
      </button>

      {expanded && (
        <div style={{ borderTop: '1px solid #f5f5f5' }}>
          {catInitiatives.length > 0 && (
            <div style={{ padding: '0.85rem 1.1rem', background: cfg.colour + '05' }}>
              <div style={{ fontSize: '0.65rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: cfg.colour, marginBottom: '0.6rem' }}>Your initiatives</div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: '0.6rem' }}>
                {catInitiatives.map((init, i) => (
                  <CommittedObjectiveBlock
                    key={init.id || `tmp-${i}`}
                    initiative={init}
                    ratingRecord={init.id ? smartRatings[init.id] : null}
                    orgRoles={orgRoles}
                    catColour={cfg.colour}
                    onUpdate={onUpdateInitiative}
                    onReRate={onReRate}
                  />
                ))}
              </div>
            </div>
          )}
          {visibleDrafts.length > 0 && (
            <div style={{ padding: '0.85rem 1.1rem' }}>
              <div style={{ fontSize: '0.65rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: '#aaa', marginBottom: '0.5rem' }}>
                {catInitiatives.length > 0 ? 'More drafts to consider' : 'Draft objectives — edit and confirm any that fit'}
              </div>
              {visibleDrafts.map(draft => (
                <DraftObjective
                  key={draft.id}
                  draft={draft}
                  category={catKey}
                  orgRoles={orgRoles}
                  onCommit={onCommitDraft}
                  onDismiss={onDismissDraft}
                />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Initiative Card (Door 1 result) ──────────────────────────────
function InitiativeCard({ initiative, index, onGenerateObjectives, objectivesState }) {
  const [expanded, setExpanded] = useState(false);
  const state = objectivesState[index] || {};
  const catCfg = CATEGORY_CONFIG[initiative.category];
  const catColour = catCfg?.colour || GURU_COLOUR;

  return (
    <div style={{ background: 'white', borderRadius: 12, border: '1px solid #e8e8f0', overflow: 'hidden', marginBottom: '0.85rem' }}>
      <div style={{ padding: '1.1rem 1.25rem', display: 'flex', alignItems: 'flex-start', gap: '0.75rem' }}>
        <div style={{ width: 28, height: 28, borderRadius: '50%', background: GURU_COLOUR + '18', border: `1px solid ${GURU_COLOUR}44`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.78rem', fontWeight: 700, color: GURU_COLOUR, flexShrink: 0, marginTop: 2 }}>
          {index + 1}
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap', marginBottom: '0.3rem' }}>
            <span style={{ fontSize: '0.92rem', fontWeight: 700, color: '#1a1a2e' }}>{initiative.title}</span>
            <PhaseBadge phase={initiative.phase} />
            {catCfg && (
              <span style={{ fontSize: '0.65rem', fontWeight: 600, padding: '2px 8px', borderRadius: 20, background: catColour + '15', color: catColour, border: `1px solid ${catColour}33` }}>
                {catCfg.icon} {catCfg.label}
              </span>
            )}
          </div>
          <p style={{ fontSize: '0.8rem', color: '#555', lineHeight: 1.55, margin: 0 }}>{initiative.description}</p>
          <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap', marginTop: '0.6rem' }}>
            <ValueChip label="Value" value={initiative.value} />
            <ValueChip label="Deliverability" value={initiative.deliverability} />
            <ValueChip label="Urgency" value={initiative.urgency} />
          </div>
          {initiative.priority_rationale && (
            <p style={{ fontSize: '0.72rem', color: '#aaa', margin: '0.5rem 0 0', fontStyle: 'italic' }}>{initiative.priority_rationale}</p>
          )}
        </div>
        <button onClick={() => setExpanded(e => !e)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '0.72rem', color: '#aaa', padding: '0.25rem', flexShrink: 0 }}>
          {expanded ? '▲' : '▼'}
        </button>
      </div>

      {expanded && Array.isArray(initiative.immediate_steps) && initiative.immediate_steps.length > 0 && (
        <div style={{ padding: '0 1.25rem 0.75rem 1.25rem', borderTop: '1px solid #f5f5f5' }}>
          <div style={{ fontSize: '0.7rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: '#aaa', margin: '0.75rem 0 0.4rem' }}>Immediate steps</div>
          {initiative.immediate_steps.map((step, i) => (
            <div key={i} style={{ display: 'flex', gap: '0.5rem', alignItems: 'flex-start', marginBottom: '0.3rem' }}>
              <span style={{ color: GURU_COLOUR, fontWeight: 700, fontSize: '0.78rem', flexShrink: 0, marginTop: 1 }}>→</span>
              <span style={{ fontSize: '0.8rem', color: '#444', lineHeight: 1.4 }}>{step}</span>
            </div>
          ))}
        </div>
      )}

      <div style={{ padding: '0.75rem 1.25rem', borderTop: '1px solid #f5f5f5', background: '#fafafa' }}>
        {state.saved ? (
          <div style={{ fontSize: '0.78rem', color: '#1D9E75', fontWeight: 600 }}>
            ✓ {state.objectives?.length || 0} SMART objective{state.objectives?.length !== 1 ? 's' : ''} saved
          </div>
        ) : state.generating ? (
          <div style={{ fontSize: '0.78rem', color: '#aaa', fontStyle: 'italic' }}>Generating objectives…</div>
        ) : (
          <button onClick={() => onGenerateObjectives(index, initiative)} style={{ background: GURU_COLOUR, color: 'white', border: 'none', borderRadius: 8, padding: '0.5rem 1.1rem', fontSize: '0.78rem', fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}>
            Generate SMART objectives →
          </button>
        )}
      </div>
    </div>
  );
}

// ── Refine Dialogue Modal ────────────────────────────────────────
function RefineDialogue({ objective, initiativeIndex, objIndex, onClose, onAcceptRefined }) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [currentObj, setCurrentObj] = useState(objective);
  const chatRef = useRef(null);

  useEffect(() => {
    if (chatRef.current) chatRef.current.scrollTop = chatRef.current.scrollHeight;
  }, [messages, loading]);

  const send = async () => {
    if (!input.trim() || loading) return;
    const msg = input.trim();
    setInput('');
    const newMessages = [...messages, { role: 'user', content: msg }];
    setMessages(newMessages);
    setLoading(true);
    try {
      const res = await axios.post(`${DELIVERY_API}/objectives/0/refine`, { objective: currentObj, message: msg, history: messages });
      const { reply, updatedObjective } = res.data;
      setMessages([...newMessages, { role: 'assistant', content: reply }]);
      if (updatedObjective) setCurrentObj(prev => ({ ...prev, ...updatedObjective }));
    } catch (e) {
      setMessages([...newMessages, { role: 'assistant', content: 'Sorry, something went wrong. Please try again.' }]);
    }
    setLoading(false);
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1.5rem' }}>
      <div style={{ background: 'white', borderRadius: 16, width: '100%', maxWidth: 640, maxHeight: '85vh', display: 'flex', flexDirection: 'column', overflow: 'hidden', boxShadow: '0 8px 40px rgba(0,0,0,0.18)' }}>
        <div style={{ padding: '1.1rem 1.5rem', borderBottom: '1px solid #f0f0f0', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div>
            <div style={{ fontSize: '0.85rem', fontWeight: 700, color: '#1a1a2e' }}>Refine objective</div>
            <div style={{ fontSize: '0.72rem', color: '#aaa', marginTop: '0.1rem' }}>Tell GuruOne what to change — it will improve and show you the updated version</div>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '1.1rem', color: '#aaa', padding: '0.25rem' }}>✕</button>
        </div>
        <div style={{ padding: '0.85rem 1.5rem', background: '#fafafa', borderBottom: '1px solid #f0f0f0' }}>
          <div style={{ fontSize: '0.65rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: '#aaa', marginBottom: '0.3rem' }}>Current objective</div>
          <p style={{ fontSize: '0.82rem', color: '#1a1a2e', margin: 0, fontWeight: 500, lineHeight: 1.5 }}>{currentObj.statement}</p>
        </div>
        <div ref={chatRef} style={{ flex: 1, overflowY: 'auto', padding: '1rem 1.5rem', display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
          {messages.length === 0 && (
            <p style={{ fontSize: '0.78rem', color: '#bbb', fontStyle: 'italic', textAlign: 'center', marginTop: '1rem' }}>
              Tell GuruOne what you want to change — the metric, timeframe, owner, or anything else.
            </p>
          )}
          {messages.map((m, i) => (
            <div key={i} style={{ alignSelf: m.role === 'user' ? 'flex-end' : 'flex-start', maxWidth: '85%', padding: '0.65rem 0.9rem', borderRadius: 10, background: m.role === 'user' ? GURU_COLOUR : '#f4f4f7', color: m.role === 'user' ? 'white' : '#333', fontSize: '0.82rem', lineHeight: 1.5, whiteSpace: 'pre-wrap' }}>
              {m.content}
            </div>
          ))}
          {loading && <div style={{ alignSelf: 'flex-start', fontSize: '0.75rem', color: '#aaa', fontStyle: 'italic' }}>GuruOne is thinking…</div>}
        </div>
        <div style={{ padding: '0.85rem 1.5rem', borderTop: '1px solid #f0f0f0', display: 'flex', gap: '0.6rem', alignItems: 'flex-end' }}>
          <textarea
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey && !loading) { e.preventDefault(); send(); } }}
            placeholder="e.g. the target needs to be more specific / the metric is not measurable…"
            rows={2}
            style={{ flex: 1, padding: '0.6rem 0.75rem', border: '1.5px solid #e0e0e0', borderRadius: 8, fontSize: '0.82rem', fontFamily: 'inherit', resize: 'none', outline: 'none', boxSizing: 'border-box' }}
          />
          <button onClick={send} disabled={!input.trim() || loading} style={{ padding: '0.6rem 1rem', borderRadius: 8, background: loading || !input.trim() ? '#e0e0e0' : GURU_COLOUR, color: loading || !input.trim() ? '#aaa' : 'white', border: 'none', fontSize: '0.82rem', fontWeight: 600, cursor: loading || !input.trim() ? 'not-allowed' : 'pointer', fontFamily: 'inherit', whiteSpace: 'nowrap' }}>
            Send
          </button>
        </div>
        {messages.some(m => m.role === 'assistant') && (
          <div style={{ padding: '0.75rem 1.5rem', borderTop: '1px solid #f0f0f0', background: '#f0fdf8' }}>
            <button onClick={() => onAcceptRefined(initiativeIndex, objIndex, currentObj)} style={{ width: '100%', padding: '0.65rem', borderRadius: 8, background: '#1D9E75', color: 'white', border: 'none', fontSize: '0.82rem', fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}>
              Accept this version and save →
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Add Initiative Panel (Door 2) ────────────────────────────────
function AddInitiativePanel({ onClose, onSaved, orgRoles }) {
  const user = (() => { try { return JSON.parse(localStorage.getItem('guruone_user')); } catch { return null; } })();
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState('');
  const [driver, setDriver] = useState('');
  const [status, setStatus] = useState('in_progress');
  const [targetDate, setTargetDate] = useState('');
  const [ownerRole, setOwnerRole] = useState('');
  const [objectives, setObjectives] = useState([]);
  const [accepted, setAccepted] = useState([]);
  const [generating, setGenerating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [refineModal, setRefineModal] = useState(null);
  const [error, setError] = useState('');
  const [step, setStep] = useState('form');

  const handleGenerate = async () => {
    if (!title.trim()) { setError('Please enter an initiative title.'); return; }
    setError('');
    setGenerating(true);
    try {
      const initiative = { title: title.trim(), description: description.trim(), category, phase: 1, immediate_steps: [], priority_rationale: driver || '' };
      const res = await axios.post(`${DELIVERY_API}/initiatives/0/objectives/generate`, { initiative, orgRoles });
      const objs = res.data.objectives || [];
      setObjectives(objs);
      setAccepted(new Array(objs.length).fill(false));
      setStep('objectives');
    } catch (e) {
      setError('Something went wrong generating objectives. Please try again.');
    }
    setGenerating(false);
  };

  const handleAccept = (objIdx) => {
    setAccepted(prev => {
      const updated = [...prev];
      updated[objIdx] = true;
      return updated;
    });
  };

  const handleAcceptRefined = (iIdx, objIdx, refinedObj) => {
    setObjectives(prev => { const u = [...prev]; u[objIdx] = refinedObj; return u; });
    setAccepted(prev => { const u = [...prev]; u[objIdx] = true; return u; });
    setRefineModal(null);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const initiative = {
        title: title.trim(),
        description: description.trim(),
        category: category || 'operations',
        source: 'user_defined',
        phase: 1,
        status,
        priority_score: 50,
        target_date: targetDate || null,
        owner_role: ownerRole || null,
      };
      const initRes = await axios.post(`${DELIVERY_API}/initiatives`, { userId: user?.id, initiative });
      const initiativeId = initRes.data.id;
      if (initiativeId && objectives.length > 0) {
        await axios.post(`${DELIVERY_API}/objectives`, { userId: user?.id, initiativeId, objectives });
      }
      onSaved({ ...initiative, id: initiativeId });
      track('delivery_initiative_created', { category: initiative.category, source: 'user_defined' });
    } catch (e) {
      setError('Could not save. Please try again.');
    }
    setSaving(false);
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 150, display: 'flex', alignItems: 'flex-start', justifyContent: 'flex-end' }}>
      <div style={{ width: '100%', maxWidth: 520, height: '100vh', background: 'white', boxShadow: '-4px 0 24px rgba(0,0,0,0.12)', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <div style={{ padding: '1.25rem 1.5rem', borderBottom: '1px solid #f0f0f0', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
          <div>
            <div style={{ fontSize: '0.92rem', fontWeight: 700, color: '#1a1a2e' }}>Add an initiative</div>
            <div style={{ fontSize: '0.72rem', color: '#aaa', marginTop: '0.15rem' }}>
              {step === 'form' ? 'Tell GuruOne what you are working on' : 'Review and accept SMART objectives'}
            </div>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '1.1rem', color: '#aaa', padding: '0.25rem' }}>✕</button>
        </div>

        <div style={{ flex: 1, overflowY: 'auto', padding: '1.25rem 1.5rem' }}>
          {step === 'form' && (
            <>
              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 700, color: '#1a1a2e', marginBottom: '0.4rem' }}>
                  What is the initiative? <span style={{ color: '#DC2626' }}>*</span>
                </label>
                <input value={title} onChange={e => setTitle(e.target.value)} placeholder="e.g. Hire and onboard a Sales Director"
                  style={{ width: '100%', padding: '0.65rem 0.8rem', border: '1.5px solid #e5e7eb', borderRadius: 8, fontSize: '0.85rem', fontFamily: 'inherit', boxSizing: 'border-box', outline: 'none' }} />
              </div>

              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 700, color: '#1a1a2e', marginBottom: '0.4rem' }}>
                  What is it trying to achieve? <span style={{ fontSize: '0.72rem', fontWeight: 400, color: '#aaa' }}>(optional)</span>
                </label>
                <textarea value={description} onChange={e => setDescription(e.target.value)} placeholder="Brief description of what success looks like…" rows={3}
                  style={{ width: '100%', padding: '0.65rem 0.8rem', border: '1.5px solid #e5e7eb', borderRadius: 8, fontSize: '0.85rem', fontFamily: 'inherit', resize: 'vertical', boxSizing: 'border-box', outline: 'none' }} />
              </div>

              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 700, color: '#1a1a2e', marginBottom: '0.4rem' }}>Which category does this fall under?</label>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.4rem' }}>
                  {CATEGORY_KEYS.map(k => {
                    const cfg = CATEGORY_CONFIG[k];
                    const selected = category === k;
                    return (
                      <button key={k} onClick={() => setCategory(k)}
                        style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', padding: '0.5rem 0.75rem', borderRadius: 8, border: `1.5px solid ${selected ? cfg.colour : '#e5e7eb'}`, background: selected ? cfg.colour + '12' : 'white', cursor: 'pointer', fontFamily: 'inherit', fontSize: '0.75rem', fontWeight: selected ? 700 : 400, color: selected ? cfg.colour : '#555', textAlign: 'left' }}>
                        <span>{cfg.icon}</span><span>{cfg.label}</span>
                      </button>
                    );
                  })}
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem', marginBottom: '1rem' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 700, color: '#1a1a2e', marginBottom: '0.4rem' }}>Owner role <span style={{ fontSize: '0.7rem', fontWeight: 400, color: '#aaa' }}>(from Design Guru)</span></label>
                  <select value={ownerRole} onChange={e => setOwnerRole(e.target.value)}
                    style={{ width: '100%', padding: '0.65rem 0.8rem', border: '1.5px solid #e5e7eb', borderRadius: 8, fontSize: '0.82rem', fontFamily: 'inherit', boxSizing: 'border-box', outline: 'none', background: 'white' }}>
                    <option value="">Select an owner…</option>
                    {orgRoles.map(r => <option key={r} value={r}>{r}</option>)}
                  </select>
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 700, color: '#1a1a2e', marginBottom: '0.4rem' }}>Target date <span style={{ fontSize: '0.7rem', fontWeight: 400, color: '#aaa' }}>(optional)</span></label>
                  <input type="date" value={targetDate} onChange={e => setTargetDate(e.target.value)}
                    style={{ width: '100%', padding: '0.65rem 0.8rem', border: '1.5px solid #e5e7eb', borderRadius: 8, fontSize: '0.82rem', fontFamily: 'inherit', boxSizing: 'border-box', outline: 'none' }} />
                </div>
              </div>

              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 700, color: '#1a1a2e', marginBottom: '0.4rem' }}>What is driving this?</label>
                <select value={driver} onChange={e => setDriver(e.target.value)}
                  style={{ width: '100%', padding: '0.65rem 0.8rem', border: '1.5px solid #e5e7eb', borderRadius: 8, fontSize: '0.85rem', fontFamily: 'inherit', boxSizing: 'border-box', outline: 'none', background: 'white' }}>
                  <option value="">Select a driver…</option>
                  {DRIVER_OPTIONS.map(d => <option key={d} value={d}>{d}</option>)}
                </select>
              </div>

              <div style={{ marginBottom: '1.25rem' }}>
                <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 700, color: '#1a1a2e', marginBottom: '0.4rem' }}>Where are you with it currently?</label>
                <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap' }}>
                  {STATUS_OPTIONS.map(opt => (
                    <button key={opt.value} onClick={() => setStatus(opt.value)}
                      style={{ padding: '0.4rem 0.85rem', borderRadius: 20, border: `1.5px solid ${status === opt.value ? GURU_COLOUR : '#e5e7eb'}`, background: status === opt.value ? GURU_COLOUR + '12' : 'white', color: status === opt.value ? GURU_COLOUR : '#555', fontSize: '0.75rem', fontWeight: status === opt.value ? 700 : 400, cursor: 'pointer', fontFamily: 'inherit' }}>
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>

              {error && <div style={{ background: '#FEE2E2', border: '1px solid #FECACA', borderRadius: 8, padding: '0.65rem 0.9rem', fontSize: '0.82rem', color: '#991B1B', marginBottom: '1rem' }}>{error}</div>}

              <button onClick={handleGenerate} disabled={!title.trim() || generating}
                style={{ width: '100%', padding: '0.85rem', borderRadius: 10, background: !title.trim() || generating ? '#e0e0e0' : GURU_COLOUR, color: !title.trim() || generating ? '#aaa' : 'white', border: 'none', fontSize: '0.88rem', fontWeight: 700, cursor: !title.trim() || generating ? 'not-allowed' : 'pointer', fontFamily: 'inherit' }}>
                {generating ? 'Generating objectives…' : 'Generate SMART objectives →'}
              </button>
            </>
          )}

          {step === 'objectives' && (
            <>
              <div style={{ background: GURU_COLOUR + '10', border: `1px solid ${GURU_COLOUR}33`, borderRadius: 10, padding: '0.85rem 1rem', marginBottom: '1.25rem' }}>
                <div style={{ fontSize: '0.78rem', fontWeight: 700, color: GURU_COLOUR, marginBottom: '0.2rem' }}>{title}</div>
                {description && <div style={{ fontSize: '0.72rem', color: '#888' }}>{description}</div>}
                {(ownerRole || targetDate) && (
                  <div style={{ fontSize: '0.7rem', color: '#777', marginTop: '0.4rem' }}>
                    {ownerRole && <>👤 {ownerRole}</>} {ownerRole && targetDate && ' · '} {targetDate && <>📅 {formatTargetDate(targetDate)}</>}
                  </div>
                )}
              </div>
              <div style={{ fontSize: '0.7rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: '#aaa', marginBottom: '0.75rem' }}>
                SMART objectives — accept or refine each one
              </div>
              {objectives.map((obj, j) => (
                <ObjectiveCard key={j} obj={obj} objIndex={j} initiativeIndex={0}
                  onAccept={() => handleAccept(j)}
                  onStartRefine={(iIdx, oIdx) => setRefineModal({ initiativeIndex: iIdx, objIndex: oIdx, objective: objectives[oIdx] })}
                  accepted={accepted[j] || false} />
              ))}
              {error && <div style={{ background: '#FEE2E2', border: '1px solid #FECACA', borderRadius: 8, padding: '0.65rem 0.9rem', fontSize: '0.82rem', color: '#991B1B', marginBottom: '1rem' }}>{error}</div>}
              <div style={{ display: 'flex', gap: '0.6rem', marginTop: '0.5rem' }}>
                <button onClick={() => setStep('form')}
                  style={{ flex: 1, padding: '0.75rem', borderRadius: 10, background: 'white', color: '#555', border: '1px solid #e0e0e0', fontSize: '0.82rem', fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}>
                  ← Edit details
                </button>
                <button onClick={handleSave} disabled={saving}
                  style={{ flex: 2, padding: '0.75rem', borderRadius: 10, background: saving ? '#e0e0e0' : '#1D9E75', color: saving ? '#aaa' : 'white', border: 'none', fontSize: '0.82rem', fontWeight: 700, cursor: saving ? 'not-allowed' : 'pointer', fontFamily: 'inherit' }}>
                  {saving ? 'Saving…' : 'Save initiative →'}
                </button>
              </div>
            </>
          )}
        </div>

      </div>

      {refineModal && (
        <RefineDialogue
          objective={refineModal.objective}
          initiativeIndex={refineModal.initiativeIndex}
          objIndex={refineModal.objIndex}
          onClose={() => setRefineModal(null)}
          onAcceptRefined={handleAcceptRefined}
        />
      )}
    </div>
  );
}

// ── Review cycle: header icon + slide-in editor ──────────────────
const REVIEW_CYCLE_KEY = 'guruone_review_cycle';
const REVIEW_PROGRESS_DESC = "A regular check-in on your initiatives: what's on track, what's stalled, and what decisions need to be made.";
const REVIEW_STRATEGIC_DESC = "A deeper look at what's changed in your market and business — and whether your direction, design and resources need to shift accordingly.";
const REVIEW_PROGRESS_OPTIONS = [
  { value: 'monthly', label: 'Monthly' },
  { value: 'quarterly', label: 'Quarterly' },
];
const REVIEW_STRATEGIC_OPTIONS = [
  { value: 'quarterly', label: 'Quarterly' },
  { value: 'biannual', label: 'Bi-annual' },
  { value: 'annual', label: 'Annual' },
];
const REVIEW_CADENCE_MONTHS = { monthly: 1, quarterly: 3, biannual: 6, annual: 12 };
const REVIEW_NICE_DATE = new Intl.DateTimeFormat('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });

function readReviewCycle() {
  try {
    const raw = localStorage.getItem(REVIEW_CYCLE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return (parsed && typeof parsed === 'object') ? parsed : null;
  } catch { return null; }
}

function writeReviewCycle(cycle) {
  try { localStorage.setItem(REVIEW_CYCLE_KEY, JSON.stringify(cycle)); } catch { /* non-fatal */ }
}

function addReviewCadenceMonths(isoDate, cadence) {
  const months = REVIEW_CADENCE_MONTHS[cadence];
  if (!months || !isoDate) return null;
  const d = new Date(isoDate);
  if (!isFinite(d.getTime())) return null;
  d.setMonth(d.getMonth() + months);
  return d.toISOString().slice(0, 10);
}

function formatReviewDate(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (!isFinite(d.getTime())) return '';
  return REVIEW_NICE_DATE.format(d);
}

// ── Shared "How to use" section block — icon + bigger title + body
function HowToSection({ Icon, colour, title, children, isLast }) {
  return (
    <div style={{ paddingBottom: isLast ? 0 : '1rem', marginBottom: isLast ? 0 : '1rem', borderBottom: isLast ? 'none' : '1px solid #f0f0f0' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.55rem', marginBottom: '0.45rem' }}>
        <Icon colour={colour} />
        <h3 style={{ fontSize: '0.92rem', fontWeight: 700, color: '#1a1a2e', margin: 0, letterSpacing: '-0.01em' }}>{title}</h3>
      </div>
      <div style={{ fontSize: '0.82rem', color: '#444', lineHeight: 1.6 }}>{children}</div>
    </div>
  );
}

// ── How to use Delivery Guru — slide-in panel ────────────────────
function HowToUseDeliveryPanel({ onClose }) {
  const accent = GURU_COLOUR;
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 200, display: 'flex', alignItems: 'flex-start', justifyContent: 'flex-end' }}>
      <div style={{ width: '100%', maxWidth: 380, height: '100vh', background: 'white', boxShadow: '-4px 0 24px rgba(0,0,0,0.12)', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <div style={{ padding: '1.1rem 1.4rem 0.9rem', borderBottom: '1px solid #f0f0f0', display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexShrink: 0 }}>
          <div>
            <div style={{ fontSize: '0.62rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: '#aaa' }}>Help</div>
            <div style={{ fontSize: '1.05rem', fontWeight: 700, color: '#1a1a2e', marginTop: 2 }}>How to use Delivery Guru</div>
          </div>
          <button onClick={onClose} aria-label="Close" style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '1.05rem', color: '#aaa', padding: '0.2rem 0.4rem', lineHeight: 1, fontFamily: 'inherit' }}>✕</button>
        </div>

        <div style={{ flex: 1, overflowY: 'auto', padding: '1.1rem 1.4rem' }}>
          <HowToSection Icon={PurposeIcon} colour={accent} title="What Delivery Guru is for">
            <p style={{ margin: 0 }}>
              Delivery Guru is where direction and design become action. It is your virtual Chief of Staff — the place to build, sequence and track a prioritised plan that turns the strategy you have set in Direction Guru and Design Guru into the next sensible thing to do this quarter, this month, this week. Without it the canvases stay aspirational; with it they move.
            </p>
          </HowToSection>

          <HowToSection Icon={FlowIcon} colour={accent} title="How it works">
            <p style={{ margin: '0 0 0.6rem' }}>
              The page is built around four stages — Direction, Design, Plan, and Reviews — visible as the journey indicator at the top. The work happens in the priorities and initiatives below, organised into six categories.
            </p>
            <p style={{ margin: 0 }}>
              There are two ways in. <strong>Door 1</strong> — Generate delivery plan — reads your canvases and proposes a sequenced set of initiatives. <strong>Door 2</strong> — Add initiative — lets you add one manually if you already know what is coming. Either way, every initiative gets SMART objectives you can refine, owners pulled from your Design Guru org chart, and dates you can edit inline.
            </p>
          </HowToSection>

          <HowToSection Icon={OutcomeIcon} colour={accent} title="What you will get out of it">
            <p style={{ margin: 0 }}>
              A structured delivery plan across six categories — revenue, people, customers, product, operations and financial — with SMART-rated objectives on each initiative, owners and target dates assigned, and a review rhythm scheduled so progress does not drift. Plus a one-page delivery plan you can share with the team to keep everyone pointing the same way.
            </p>
          </HowToSection>

          <HowToSection Icon={TipsIcon} colour={accent} title="Tips for getting the most from it" isLast>
            <ul style={{ margin: 0, paddingLeft: '1.1rem' }}>
              <li style={{ marginBottom: '0.4rem' }}>If your canvases are not complete, start with Door 2 — adding what you are already working on is more useful than waiting for a perfect plan.</li>
              <li style={{ marginBottom: '0.4rem' }}>Keep the number of initiatives manageable per quarter — five focused commitments will outperform fifteen half-finished ones.</li>
              <li style={{ marginBottom: '0.4rem' }}>Set your review dates early, before the rhythm slides — the wheel at the top is there to keep this honest.</li>
              <li style={{ marginBottom: '0.4rem' }}>Use the SMART rating to sharpen each objective before you commit it — vague objectives produce vague delivery.</li>
            </ul>
          </HowToSection>
        </div>

        <div style={{ padding: '0.9rem 1.4rem', borderTop: '1px solid #f0f0f0', flexShrink: 0 }}>
          <button onClick={onClose}
            style={{ width: '100%', padding: '0.65rem', borderRadius: 8, background: 'white', border: '1.5px solid #e0e0e0', color: '#555', fontSize: '0.82rem', fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

function ReviewCyclePanel({ onClose }) {
  // Local edit state — changes are not persisted to localStorage
  // until the user clicks Save. Cancel discards everything.
  const [cycle, setCycle] = useState(() => {
    const stored = readReviewCycle();
    return {
      progress: stored?.progress || {},
      strategic: { cadence: 'annual', ...(stored?.strategic || {}) },
    };
  });
  const [editingNextDate, setEditingNextDate] = useState({ progress: false, strategic: false });

  const updateField = (type, field, value) => {
    setCycle(prev => {
      const updated = { ...prev[type], [field]: value };
      if ((field === 'cadence' || field === 'startDate') && updated.cadence && updated.startDate) {
        const computed = addReviewCadenceMonths(updated.startDate, updated.cadence);
        if (computed) updated.nextDate = computed;
      }
      return { ...prev, [type]: updated };
    });
  };

  const overrideNextDate = (type, value) => {
    setCycle(prev => ({ ...prev, [type]: { ...prev[type], nextDate: value } }));
  };

  const handleSave = () => {
    writeReviewCycle(cycle);
    onClose();
  };

  // Shared input/label styles so both columns line up exactly.
  const inputStyle = {
    width: '100%',
    height: 36,
    padding: '0 0.7rem',
    border: '1px solid #e5e7eb',
    borderRadius: 8,
    fontSize: '0.82rem',
    fontFamily: 'inherit',
    boxSizing: 'border-box',
    background: 'white',
    color: '#1a1a2e',
    outline: 'none',
  };
  const fieldLabelStyle = {
    fontSize: 11,
    fontWeight: 700,
    color: '#666',
    textTransform: 'uppercase',
    letterSpacing: '0.06em',
    display: 'block',
    marginBottom: '0.3rem',
  };

  // Helper to render the "next review" cell for a given review type.
  const NextReviewCell = ({ type, accentColour }) => {
    const entry = cycle[type];
    if (!entry?.nextDate) {
      return (
        <span style={{ fontSize: 12, color: '#aaa', fontStyle: 'italic' }}>
          Set cadence and start date to schedule the next review
        </span>
      );
    }
    const isEditing = editingNextDate[type];
    if (isEditing) {
      return (
        <input
          type="date"
          autoFocus
          value={entry.nextDate}
          onChange={e => { if (e.target.value) overrideNextDate(type, e.target.value); }}
          onBlur={() => setEditingNextDate(s => ({ ...s, [type]: false }))}
          style={{ ...inputStyle, height: 32 }}
        />
      );
    }
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', minHeight: 32 }}>
        <span style={{ fontSize: '0.9rem', fontWeight: 700, color: accentColour }}>
          {formatReviewDate(entry.nextDate)}
        </span>
        <button
          onClick={() => setEditingNextDate(s => ({ ...s, [type]: true }))}
          style={{ background: 'none', border: 'none', color: '#888', fontSize: 12, cursor: 'pointer', padding: 0, fontFamily: 'inherit', textDecoration: 'underline' }}
        >
          Edit
        </button>
      </div>
    );
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 150, display: 'flex', alignItems: 'flex-start', justifyContent: 'flex-end' }}>
      <div style={{ width: '100%', maxWidth: 560, height: '100vh', background: 'white', boxShadow: '-4px 0 24px rgba(0,0,0,0.12)', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>

        {/* Header — title and descriptor full width, ✕ in its own row */}
        <div style={{ padding: '1.25rem 1.5rem 1rem', borderBottom: '1px solid #f0f0f0', flexShrink: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div style={{ fontSize: '1rem', fontWeight: 700, color: '#1a1a2e' }}>Your review cycle</div>
            <button onClick={onClose} aria-label="Close" style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '1.1rem', color: '#aaa', padding: '0.25rem' }}>✕</button>
          </div>
          <div style={{ fontSize: '0.78rem', color: '#666', marginTop: '0.4rem', lineHeight: 1.55 }}>
            Set a regular rhythm for reviewing progress and revisiting your direction. Progress reviews keep your initiatives on track. Strategic reviews check whether your direction still fits your market.
          </div>
        </div>

        {/* Body */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '1.25rem 1.5rem' }}>

          {/* Concentric wheels reflecting the live edit state */}
          <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '1.5rem' }}>
            <ConcentricReviewWheels cycle={cycle} size="panel" />
          </div>

          {/* Two-column form on a single grid so corresponding rows align */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            columnGap: 20,
            rowGap: '0.85rem',
            alignItems: 'start',
          }}>
            {/* Row: review-type heading + description */}
            <div>
              <div style={{ fontSize: '0.92rem', fontWeight: 700, color: '#D97706', marginBottom: '0.3rem' }}>Progress review</div>
              <p style={{ fontStyle: 'italic', color: '#9CA3AF', fontSize: 12, lineHeight: 1.45, margin: 0 }}>{REVIEW_PROGRESS_DESC}</p>
            </div>
            <div>
              <div style={{ fontSize: '0.92rem', fontWeight: 700, color: '#7C3AED', marginBottom: '0.3rem' }}>Strategic review</div>
              <p style={{ fontStyle: 'italic', color: '#9CA3AF', fontSize: 12, lineHeight: 1.45, margin: 0 }}>{REVIEW_STRATEGIC_DESC}</p>
            </div>

            {/* Row: cadence label */}
            <label style={fieldLabelStyle}>Cadence</label>
            <label style={fieldLabelStyle}>Cadence</label>

            {/* Row: cadence selects */}
            <select
              value={cycle.progress?.cadence || ''}
              onChange={e => updateField('progress', 'cadence', e.target.value)}
              style={inputStyle}
            >
              <option value="" disabled>Select cadence…</option>
              {REVIEW_PROGRESS_OPTIONS.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
            </select>
            <select
              value={cycle.strategic?.cadence || ''}
              onChange={e => updateField('strategic', 'cadence', e.target.value)}
              style={inputStyle}
            >
              <option value="" disabled>Select cadence…</option>
              {REVIEW_STRATEGIC_OPTIONS.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
            </select>

            {/* Row: start-date label */}
            <label style={fieldLabelStyle}>Cycle start date</label>
            <label style={fieldLabelStyle}>Cycle start date</label>

            {/* Row: start-date inputs */}
            <input
              type="date"
              value={cycle.progress?.startDate || ''}
              onChange={e => updateField('progress', 'startDate', e.target.value)}
              style={inputStyle}
            />
            <input
              type="date"
              value={cycle.strategic?.startDate || ''}
              onChange={e => updateField('strategic', 'startDate', e.target.value)}
              style={inputStyle}
            />

            {/* Row: next-review label */}
            <label style={fieldLabelStyle}>Next review</label>
            <label style={fieldLabelStyle}>Next review</label>

            {/* Row: next-review value/editor */}
            <NextReviewCell type="progress" accentColour="#D97706" />
            <NextReviewCell type="strategic" accentColour="#7C3AED" />
          </div>
        </div>

        {/* Footer — Save (green) + Cancel (muted) */}
        <div style={{ padding: '1rem 1.5rem 1.25rem', borderTop: '1px solid #f0f0f0', flexShrink: 0, display: 'flex', gap: '0.6rem' }}>
          <button
            onClick={onClose}
            style={{
              flex: 1,
              padding: '0.7rem',
              borderRadius: 10,
              border: '1px solid #e0e0e0',
              background: 'white',
              color: '#666',
              fontSize: '0.82rem',
              fontWeight: 600,
              cursor: 'pointer',
              fontFamily: 'inherit',
            }}
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            style={{
              flex: 2,
              padding: '0.7rem',
              borderRadius: 10,
              border: 'none',
              background: '#1D9E75',
              color: 'white',
              fontSize: '0.82rem',
              fontWeight: 700,
              cursor: 'pointer',
              fontFamily: 'inherit',
            }}
          >
            Save
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Delivery journey track (4-stage indicator) ──────────────────
// Mirrors the Direction / Design journey indicators visually. Stages
// are rendered unconditionally — empty/grey when no progress yet.
//
//   1. Direction — % of the 4 critical Direction Canvas sections
//   2. Design    — % of the 4 critical Design Canvas sections
//   3. Plan      — bucketed initiative count (0/0%, 1–2/25%,
//                  3–5/50%, 6–9/75%, 10+/100%)
//   4. Reviews   — review-cycle dates set (0/0%, 1/50%, 2/100%)
//
// Clicking Direction / Design navigates to the relevant canvas;
// clicking Reviews opens the review cycle slide-in panel; Plan is a
// no-op (it represents the current page).
function deliveryStageColour(filled, total) {
  if (filled === 0) return '#9CA3AF';
  const pct = filled / total;
  if (pct >= 1) return '#1D9E75';
  if (pct >= 0.5) return '#D97706';
  return '#DC2626';
}

function DeliveryJourneyTrack({ strategyData, designData, savedInitiatives, onOpenReview }) {
  const navigate = useNavigate();

  const directionFilled = DIRECTION_CHECKS.filter(c => isSectionFilled(strategyData, c.key)).length;
  const designFilled = DESIGN_CHECKS.filter(c => isSectionFilled(designData, c.key)).length;
  const initiativeCount = savedInitiatives.length;
  const planFilled = initiativeCount === 0 ? 0
    : initiativeCount <= 2 ? 1
    : initiativeCount <= 5 ? 2
    : initiativeCount <= 9 ? 3
    : 4;
  const cycle = readReviewCycle();
  const reviewsFilled = (cycle?.progress?.nextDate ? 1 : 0) + (cycle?.strategic?.nextDate ? 1 : 0);

  const stages = [
    { key: 'direction', title: 'Direction', subtitle: 'Where you are going',     filled: directionFilled, total: DIRECTION_CHECKS.length, onClick: () => navigate('/strategy') },
    { key: 'design',    title: 'Design',    subtitle: 'How you operate',         filled: designFilled,    total: DESIGN_CHECKS.length,    onClick: () => navigate('/operating-model') },
    { key: 'plan',      title: 'Plan',      subtitle: 'Initiatives in flight',  filled: planFilled,      total: 4, onClick: null },
    { key: 'reviews',   title: 'Reviews',   subtitle: 'Cadence in place',       filled: reviewsFilled,   total: 2, onClick: onOpenReview },
  ];

  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', width: '100%', margin: '0 0 1.75rem', padding: '0.75rem 0.25rem 0' }}>
      {stages.map((stage, i) => {
        const colour = deliveryStageColour(stage.filled, stage.total);
        const fullyFilled = stage.filled === stage.total;
        const interactive = !!stage.onClick;
        return (
          <div key={stage.key} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', position: 'relative', minWidth: 0 }}>
            {i < stages.length - 1 && (
              <div style={{
                position: 'absolute', top: 9,
                left: 'calc(50% + 12px)', right: 'calc(-50% + 12px)',
                height: '1.5px',
                background: fullyFilled ? colour : '#e0e0e0',
                zIndex: 0,
              }} />
            )}
            <button
              onClick={stage.onClick || (() => {})}
              disabled={!interactive}
              aria-label={`${stage.title}: ${stage.filled} of ${stage.total}`}
              title={`${stage.title} — ${stage.filled} of ${stage.total}`}
              style={{
                background: 'none', border: 'none', padding: 0,
                cursor: interactive ? 'pointer' : 'default',
                fontFamily: 'inherit', width: 'auto',
                display: 'flex', flexDirection: 'column', alignItems: 'center',
                position: 'relative', zIndex: 1,
              }}
            >
              <div style={{
                width: 20, height: 20, borderRadius: '50%',
                background: stage.filled > 0 ? colour : 'white',
                border: `2px solid ${colour}`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                marginBottom: 6, flexShrink: 0,
              }}>
                {fullyFilled ? (
                  <svg viewBox="0 0 8 8" width="9" height="9">
                    <polyline points="1,4 3,6.5 7,1.5" fill="none" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                ) : stage.filled > 0 ? (
                  <div style={{ width: 7, height: 7, borderRadius: '50%', background: 'white' }} />
                ) : null}
              </div>
              <div style={{ fontSize: 12, fontWeight: 700, color: stage.filled > 0 ? '#1a1a2e' : '#666', textAlign: 'center', lineHeight: 1.2 }}>
                {stage.title}
              </div>
              <div style={{ fontSize: 10.5, color: '#999', textAlign: 'center', marginTop: 3, lineHeight: 1.35, padding: '0 0.4rem' }}>
                {stage.subtitle}
              </div>
              <div style={{ fontSize: 10, color: colour, fontWeight: 700, marginTop: 4, letterSpacing: '0.04em' }}>
                {stage.filled}/{stage.total}
              </div>
            </button>
          </div>
        );
      })}
    </div>
  );
}

// ── Delivery upload panel — "Import existing plans and priorities" ─
// Hits /api/upload/combined and pre-fills both upstream canvases
// (Direction + Design). Initiative-shaped extraction is still on the
// backlog — when it lands it can sit alongside the dual-canvas
// suggestions in the same review. For now: leader uploads here, the
// strategic context behind their delivery work gets captured upstream.
function DeliveryGlobalUploadPanel() {
  const [files, setFiles] = useState([]);
  const [websiteUrl, setWebsiteUrl] = useState('');
  const [urlEditable, setUrlEditable] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);
  const [accepted, setAccepted] = useState({});
  const [helpOpen, setHelpOpen] = useState(false);
  const [scope, setScope] = useState('both');
  const fileInputRef = useRef(null);
  const ACCEPTED = '.pdf,.docx,.pptx,.xlsx,.txt,.md,.png,.jpg,.jpeg,.gif,.webp';

  useEffect(() => {
    try {
      const user = JSON.parse(localStorage.getItem('guruone_user'));
      if (user?.websiteUrl) {
        setWebsiteUrl(user.websiteUrl);
        setUrlEditable(false);
      }
    } catch { /* non-fatal */ }
  }, []);

  const STRAT_LABELS = {
    founder_purpose: 'Founder Purpose', values: 'Values', mission: 'Mission', vision: 'Vision',
    value_proposition: 'Value Proposition', customers: 'Customers', competitors: 'Competitors',
    differentiators: 'Differentiators', go_to_market: 'Go to Market', revenue_model: 'Revenue Model',
    cost_model: 'Cost Model', current_challenges: 'Current Challenges',
  };
  const STRAT_ICONS = {
    founder_purpose: '🌱', values: '🤝', mission: '🎯', vision: '🔭',
    value_proposition: '💎', customers: '👥', competitors: '⚔️',
    differentiators: '✨', go_to_market: '🚀', revenue_model: '💰',
    cost_model: '📊', current_challenges: '🚧',
  };
  const OM_LABELS = {
    people_roles: 'People, Roles & Structure', culture_leadership: 'Culture & Leadership',
    location_infrastructure: 'Location & Infrastructure', suppliers_partners: 'Suppliers & Partners',
    customer_interactions: 'Customer Interactions', process_ways_of_work: 'Process & Ways of Work',
    governance_controls: 'Governance & Review Rhythm', finance_commercial: 'Finance & Commercial',
    it_data_security: 'IT & Data Security', portfolio: 'Product & Service Portfolio',
    business_development: 'Business Development', marketing_brand: 'Marketing & Brand',
  };
  const OM_ICONS = {
    people_roles: '👥', culture_leadership: '🌱', location_infrastructure: '🏢',
    suppliers_partners: '🤝', customer_interactions: '💬', process_ways_of_work: '⚙️',
    governance_controls: '🔄', finance_commercial: '💰', it_data_security: '🔒',
    portfolio: '🗂️', business_development: '🤝', marketing_brand: '📣',
  };
  const UPLOAD_HINTS = [
    { doc: 'Business plan or strategy deck', unlocks: 'Founder purpose, values, mission, vision, value proposition' },
    { doc: 'Pitch deck or sales collateral', unlocks: 'Value proposition, competitors, differentiators, go-to-market' },
    { doc: 'Project plan / OKRs / board pack', unlocks: 'Current challenges, finance & commercial, governance' },
    { doc: 'P&L / management accounts', unlocks: 'Revenue model, cost model, finance & commercial' },
    { doc: 'Customer list or case studies', unlocks: 'Customers, customer interactions, differentiators' },
    { doc: 'Org chart or role descriptions', unlocks: 'People & roles, culture, governance' },
    { doc: 'Process docs, SOPs or playbooks', unlocks: 'Process & ways of work, suppliers, quality' },
    { doc: 'Marketing collateral or website', unlocks: 'Marketing & brand, GTM, value proposition' },
  ];
  const CONF_STYLE = {
    high:   { label: 'High confidence',   bg: '#f0fdf8', border: '#a7f3d0', color: '#065f46' },
    medium: { label: 'Medium confidence', bg: '#fffbeb', border: '#fde68a', color: '#78350f' },
    low:    { label: 'Low confidence',    bg: '#fef2f2', border: '#fecaca', color: '#991b1b' },
  };

  const handleFiles = (e) => {
    const incoming = Array.from(e.target.files);
    setFiles(prev => {
      const existingNames = new Set(prev.map(f => f.name));
      return [...prev, ...incoming.filter(f => !existingNames.has(f.name))];
    });
    setResult(null);
    e.target.value = '';
  };
  const removeFile = (name) => setFiles(prev => prev.filter(f => f.name !== name));

  const hasInput = files.length > 0 || websiteUrl.trim().length > 0;

  const handleUpload = async () => {
    if (!hasInput || uploading) return;
    setUploading(true);
    setResult(null);
    try {
      const formData = new FormData();
      files.forEach(f => formData.append('files', f));
      if (websiteUrl.trim()) formData.append('url', websiteUrl.trim());
      formData.append('scope', scope);
      const res = await axios.post(`${API_BASE}/upload/combined`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 180000,
      });
      const stratSecs = res.data.strategy?.sections || {};
      const omSecs = res.data.operating_model?.sections || {};
      if (Object.keys(stratSecs).length === 0 && Object.keys(omSecs).length === 0) {
        // Honest feedback instead of an empty review screen.
        alert("We couldn't pull anything usable from what you shared. This can happen with a scanned/image-only PDF we couldn't read, a password-protected file, or a file with very little text. Try a clearer or text-based file, paste your website URL, or email Glenn at glenn@guruone.ai and he'll take a look.");
        setUploading(false);
        return;
      }
      setResult(res.data);
      const init = {};
      Object.keys(stratSecs).forEach(k => { init[`strat_${k}`] = true; });
      Object.keys(omSecs).forEach(k => { init[`om_${k}`] = true; });
      setAccepted(init);
    } catch (e) {
      alert(e.response?.data?.error || 'Upload failed. Please try again.');
    }
    setUploading(false);
  };

  const handleSave = () => {
    const stratToSave = {};
    const omToSave = {};
    Object.keys(accepted).forEach(k => {
      if (!accepted[k]) return;
      if (k.startsWith('strat_')) {
        const key = k.slice(6);
        if (result?.strategy?.sections?.[key]) stratToSave[key] = result.strategy.sections[key];
      } else if (k.startsWith('om_')) {
        const key = k.slice(3);
        if (result?.operating_model?.sections?.[key]) omToSave[key] = { current: result.operating_model.sections[key] };
      }
    });

    // Direction — merge into user-scoped guruone_strategy. The merge
    // helper preserves narratives, transcripts and evaluations on any
    // section the leader has already deep-dived.
    if (Object.keys(stratToSave).length > 0) {
      try {
        const user = JSON.parse(localStorage.getItem('guruone_user'));
        const userKey = user?.id ? `user_${user.id}` : 'default';
        const raw = localStorage.getItem('guruone_strategy');
        const all = raw ? JSON.parse(raw) : {};
        const existing = (all[userKey] && typeof all[userKey] === 'object') ? all[userKey] : {};
        const mergedStrat = {};
        Object.entries(stratToSave).forEach(([key, incoming]) => {
          mergedStrat[key] = mergeSectionPreservingMeta(existing[key], incoming);
        });
        all[userKey] = { ...existing, ...mergedStrat, lastUpdated: new Date().toISOString() };
        localStorage.setItem('guruone_strategy', JSON.stringify(all));
      } catch { /* non-fatal */ }
    }

    // Design — same protection on the OM side.
    if (Object.keys(omToSave).length > 0) {
      try {
        const raw = localStorage.getItem('guruone_operating_model');
        const existing = raw ? JSON.parse(raw) : {};
        const next = { ...existing };
        Object.entries(omToSave).forEach(([key, payload]) => {
          next[key] = mergeSectionPreservingMeta(existing[key], payload);
        });
        localStorage.setItem('guruone_operating_model', JSON.stringify(next));
      } catch { /* non-fatal */ }
    }

    track('upload_combined_save', { from: 'delivery', strategy: Object.keys(stratToSave).length, om: Object.keys(omToSave).length, scope });
    setResult(null);
    setFiles([]);
    setWebsiteUrl('');
  };

  const ORDER = ['high','medium','low'];
  const getConf = (key, conf) => {
    if (conf[key]) return conf[key];
    const hits = Object.entries(conf).filter(([k]) => k.startsWith(key)).map(([,v]) => v);
    if (!hits.length) return 'medium';
    return hits.sort((a,b) => ORDER.indexOf(b) - ORDER.indexOf(a))[0];
  };
  const buildPreview = (data) => {
    if (!data || typeof data !== 'object') return String(data).slice(0, 80);
    if (data.entries) return `${data.entries.length} entr${data.entries.length === 1 ? 'y' : 'ies'} found`;
    const vals = Object.entries(data)
      .filter(([,v]) => v && !String(v).includes('unclear'))
      .slice(0, 3)
      .map(([k, v]) => {
        const display = Array.isArray(v) ? v.join(', ') : typeof v === 'object' ? JSON.stringify(v).slice(0,40) : String(v).slice(0,60);
        return `${k.replace(/_/g,' ')}: ${display}`;
      });
    return vals.join(' · ') || 'Values extracted';
  };
  const renderRow = (key, data, conf, icon, label, canvas, isLast) => {
    const cs = CONF_STYLE[conf] || CONF_STYLE.medium;
    const acceptKey = `${canvas}_${key}`;
    return (
      <div key={acceptKey} style={{ padding: '0.75rem 1.25rem', borderBottom: isLast ? 'none' : '1px solid #f0f0f0', background: accepted[acceptKey] ? 'white' : '#fafafa' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.65rem', flexWrap: 'wrap' }}>
          <span style={{ fontSize: '0.82rem', fontWeight: 700, color: '#1a1a2e', flex: 1, minWidth: 120 }}>
            {icon && <span style={{ marginRight: '0.35rem' }}>{icon}</span>}
            {label}
          </span>
          <span style={{ fontSize: '0.62rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em', padding: '2px 7px', borderRadius: 10, background: cs.bg, border: `1px solid ${cs.border}`, color: cs.color, whiteSpace: 'nowrap' }}>
            {cs.label}
          </span>
          <button onClick={() => setAccepted(prev => ({ ...prev, [acceptKey]: !prev[acceptKey] }))}
            style={{ padding: '0.25rem 0.65rem', borderRadius: 6, fontSize: '0.72rem', fontWeight: 600, border: accepted[acceptKey] ? `1.5px solid ${GURU_COLOUR}` : '1.5px solid #ddd', background: accepted[acceptKey] ? '#FFF3E1' : 'white', color: accepted[acceptKey] ? '#7B4A0F' : '#888', cursor: 'pointer', fontFamily: 'inherit', width: 'auto', whiteSpace: 'nowrap' }}>
            {accepted[acceptKey] ? '✓ Accepted' : 'Accept'}
          </button>
        </div>
        <div style={{ fontSize: '0.7rem', color: '#888', marginTop: '0.3rem', lineHeight: 1.4 }}>{buildPreview(data)}</div>
      </div>
    );
  };

  if (result && (Object.keys(result.strategy?.sections || {}).length > 0 || Object.keys(result.operating_model?.sections || {}).length > 0)) {
    const stratSections = result.strategy?.sections || {};
    const stratConfidence = result.strategy?.confidence || {};
    const omSections = result.operating_model?.sections || {};
    const omConfidence = result.operating_model?.confidence || {};
    const stratKeys = Object.keys(stratSections);
    const omKeys = Object.keys(omSections);
    const acceptedCount = Object.entries(accepted)
      .filter(([k, v]) => v)
      .filter(([k]) => {
        if (k.startsWith('strat_')) return !!stratSections[k.slice(6)];
        if (k.startsWith('om_')) return !!omSections[k.slice(3)];
        return false;
      }).length;

    let existingStrategySnapshot = {};
    let existingOMSnapshot = {};
    try {
      const sUser = JSON.parse(localStorage.getItem('guruone_user') || '{}');
      const userKey = sUser?.id ? `user_${sUser.id}` : 'default';
      const sRaw = localStorage.getItem('guruone_strategy');
      const sAll = sRaw ? JSON.parse(sRaw) : {};
      existingStrategySnapshot = (sAll[userKey] && typeof sAll[userKey] === 'object') ? sAll[userKey] : {};
      const oRaw = localStorage.getItem('guruone_operating_model');
      existingOMSnapshot = oRaw ? JSON.parse(oRaw) : {};
    } catch { /* non-fatal */ }
    const protectedDeepDives = countDeepDiveSections(existingStrategySnapshot) + countDeepDiveSections(existingOMSnapshot);

    return (
      <div style={{ marginBottom: '1.5rem' }}>
        <div style={{ background: '#FFF3E1', border: `1.5px solid ${GURU_COLOUR}`, borderRadius: '12px 12px 0 0', padding: '1rem 1.25rem', borderBottom: 'none' }}>
          <div style={{ fontSize: '0.88rem', fontWeight: 700, color: '#7B4A0F' }}>
            Found information across {stratKeys.length + omKeys.length} section{(stratKeys.length + omKeys.length) !== 1 ? 's' : ''}
          </div>
          <div style={{ fontSize: '0.72rem', color: GURU_COLOUR, marginTop: '0.2rem' }}>
            Review each section — accept or reject before saving. Direction sections land on your Direction canvas; Design sections land on your Design canvas. Initiative-shaped extraction will follow.
          </div>
          {protectedDeepDives > 0 && (
            <div style={{ marginTop: '0.6rem', padding: '0.55rem 0.8rem', background: 'white', border: '1px solid #FCD34D', borderRadius: 6, fontSize: '0.7rem', color: '#7B4A0F', lineHeight: 1.5 }}>
              <strong>Your deep-dive work is safe.</strong> {protectedDeepDives} section{protectedDeepDives !== 1 ? 's have' : ' has'} a narrative or transcript captured — those will not be overwritten. Accepted suggestions will only update chip selections and short text fields.
            </div>
          )}
        </div>

        {stratKeys.length > 0 && (
          <>
            <div style={{ background: '#1a1a2e', color: 'white', padding: '0.5rem 1.25rem', fontSize: '0.7rem', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', borderLeft: `1.5px solid ${GURU_COLOUR}`, borderRight: `1.5px solid ${GURU_COLOUR}` }}>
              Direction canvas — {stratKeys.length} section{stratKeys.length !== 1 ? 's' : ''}
            </div>
            <div style={{ borderLeft: `1.5px solid ${GURU_COLOUR}`, borderRight: `1.5px solid ${GURU_COLOUR}` }}>
              {stratKeys.map((key, i) => renderRow(
                key,
                stratSections[key],
                getConf(key, stratConfidence),
                STRAT_ICONS[key],
                STRAT_LABELS[key] || key,
                'strat',
                i === stratKeys.length - 1 && omKeys.length === 0,
              ))}
            </div>
          </>
        )}

        {omKeys.length > 0 && (
          <>
            <div style={{ background: '#185FA5', color: 'white', padding: '0.5rem 1.25rem', fontSize: '0.7rem', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', borderLeft: `1.5px solid ${GURU_COLOUR}`, borderRight: `1.5px solid ${GURU_COLOUR}` }}>
              Design canvas — {omKeys.length} section{omKeys.length !== 1 ? 's' : ''}
            </div>
            <div style={{ borderLeft: `1.5px solid ${GURU_COLOUR}`, borderRight: `1.5px solid ${GURU_COLOUR}` }}>
              {omKeys.map((key, i) => renderRow(
                key,
                omSections[key],
                getConf(key, omConfidence),
                OM_ICONS[key],
                OM_LABELS[key] || key,
                'om',
                i === omKeys.length - 1,
              ))}
            </div>
          </>
        )}

        <div style={{ border: `1.5px solid ${GURU_COLOUR}`, borderTop: 'none', borderRadius: '0 0 12px 12px', height: 1 }} />

        {result.errors?.length > 0 && (
          <div style={{ fontSize: '0.72rem', color: '#BA7517', margin: '0.75rem 0' }}>⚠ {result.errors.join(' · ')}</div>
        )}

        <div style={{ display: 'flex', gap: '0.75rem', marginTop: '0.85rem' }}>
          <button onClick={handleSave}
            style={{ padding: '0.55rem 1.25rem', borderRadius: 8, background: GURU_COLOUR, color: 'white', border: 'none', fontSize: '0.85rem', fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', width: 'auto' }}>
            Save {acceptedCount} section{acceptedCount !== 1 ? 's' : ''} to canvases →
          </button>
          <button onClick={() => { setResult(null); setFiles([]); setWebsiteUrl(''); }}
            style={{ padding: '0.55rem 1rem', borderRadius: 8, background: 'white', color: '#888', border: '1px solid #ddd', fontSize: '0.82rem', cursor: 'pointer', fontFamily: 'inherit', width: 'auto' }}>
            Discard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ marginBottom: '1.5rem' }}>
      <div style={{ border: '1.5px solid #e0e0e0', borderRadius: 10, padding: '0.75rem 1rem', background: '#f9fffe' }}>
        <div style={{ fontSize: '0.78rem', fontWeight: 700, color: '#1a1a2e', marginBottom: '0.25rem' }}>
          Import existing plans and priorities
        </div>
        <div style={{ fontSize: '0.72rem', color: '#666', lineHeight: 1.5, marginBottom: '0.4rem' }}>
          Upload your business plan, board reports or planning documents. We&apos;ll pre-fill <strong>both your Direction canvas and your Design canvas</strong> with anything relevant — initiative-shaped extraction will follow.
        </div>

        <button
          type="button"
          onClick={() => setHelpOpen(o => !o)}
          aria-expanded={helpOpen}
          style={{
            background: 'none', border: 'none', color: GURU_COLOUR, cursor: 'pointer',
            padding: 0, fontSize: '0.72rem', fontWeight: 600, fontFamily: 'inherit',
            textDecoration: 'underline', textUnderlineOffset: 2, marginBottom: '0.55rem', width: 'auto',
          }}>
          {helpOpen ? 'Hide what helps and why' : 'What helps and why →'}
        </button>
        {helpOpen && (
          <div style={{ background: 'white', border: '1px solid #e0e0e0', borderRadius: 8, padding: '0.7rem 0.9rem', marginBottom: '0.6rem' }}>
            <div style={{ fontSize: '0.7rem', color: '#666', lineHeight: 1.5, marginBottom: '0.45rem' }}>
              Different documents unlock different parts of the canvas. Include anything you have for the areas that matter most.
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr)', gap: '0.3rem' }}>
              {UPLOAD_HINTS.map(({ doc, unlocks }) => (
                <div key={doc} style={{ display: 'flex', gap: '0.5rem', alignItems: 'baseline', fontSize: '0.72rem', lineHeight: 1.4 }}>
                  <span style={{ color: GURU_COLOUR, flexShrink: 0 }}>·</span>
                  <span style={{ fontWeight: 600, color: '#1a1a2e', flexShrink: 0 }}>{doc}</span>
                  <span style={{ color: '#888' }}>→ {unlocks}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Scope selector — populate one or both canvases. Narratives
            are always preserved regardless of scope. */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.55rem', flexWrap: 'wrap', marginBottom: '0.55rem' }}>
          <span style={{ fontSize: '0.7rem', color: '#666', fontWeight: 600 }}>Populate:</span>
          {[
            { key: 'both', label: 'Both canvases' },
            { key: 'strategy', label: 'Direction only' },
            { key: 'operating_model', label: 'Design only' },
          ].map(opt => (
            <button key={opt.key} type="button" onClick={() => setScope(opt.key)}
              style={{
                padding: '0.25rem 0.7rem', borderRadius: 14, fontSize: '0.7rem', fontWeight: 600,
                border: scope === opt.key ? `1.5px solid ${GURU_COLOUR}` : '1px solid #d1d5db',
                background: scope === opt.key ? '#FFF3E1' : 'white',
                color: scope === opt.key ? '#7B4A0F' : '#666',
                cursor: 'pointer', fontFamily: 'inherit', width: 'auto',
              }}>
              {opt.label}
            </button>
          ))}
          <span style={{ fontSize: '0.66rem', color: '#888', fontStyle: 'italic' }}>
            Narratives + deep-dive content on existing sections are always preserved.
          </span>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
          <span style={{ fontSize: '0.72rem', color: '#888', flexShrink: 0 }}>🌐 Website</span>
          {urlEditable ? (
            <input
              type="url"
              value={websiteUrl}
              onChange={e => setWebsiteUrl(e.target.value)}
              placeholder="https://yourwebsite.com"
              style={{ width: 220, height: 32, fontSize: '0.78rem', padding: '0 0.6rem', borderRadius: 6, border: '1px solid #ddd', fontFamily: 'inherit', boxSizing: 'border-box', outline: 'none' }}
            />
          ) : (
            <span style={{
              display: 'inline-flex', alignItems: 'center', gap: '0.4rem',
              height: 32, padding: '0 0.7rem', borderRadius: 6,
              background: '#E1F5EE', border: '1px solid #A5D6C3',
              fontSize: '0.75rem', color: '#0F6E56', fontWeight: 600,
              boxSizing: 'border-box',
            }}>
              <span style={{ color: '#1D9E75', fontWeight: 800 }}>✓</span>
              {websiteUrl}
              <button
                onClick={() => setUrlEditable(true)}
                style={{ background: 'none', border: 'none', color: '#0F6E56', fontSize: '0.7rem', cursor: 'pointer', padding: 0, marginLeft: '0.3rem', textDecoration: 'underline', fontFamily: 'inherit' }}
              >
                Edit
              </button>
            </span>
          )}
          <span style={{ color: '#ddd' }}>|</span>
          <span style={{ fontSize: '0.72rem', color: '#888', flexShrink: 0 }}>📄 Documents</span>
          <input ref={fileInputRef} type="file" accept={ACCEPTED} multiple onChange={handleFiles} style={{ display: 'none' }} />
          <button onClick={() => fileInputRef.current?.click()}
            style={{ height: 32, padding: '0 0.75rem', borderRadius: 6, border: `1px solid ${GURU_COLOUR}`, background: 'white', color: GURU_COLOUR, fontSize: '0.75rem', fontWeight: 600, cursor: 'pointer', width: 'auto', fontFamily: 'inherit', boxSizing: 'border-box' }}>
            + Add files
          </button>
          <span style={{ fontSize: '0.68rem', color: '#ccc', flexShrink: 0 }}>PDF, Word, PowerPoint, Excel, images, text</span>
          {hasInput && (
            <button onClick={handleUpload} disabled={uploading}
              style={{ marginLeft: 'auto', height: 32, padding: '0 0.9rem', borderRadius: 6, background: uploading ? '#ccc' : GURU_COLOUR, border: 'none', color: 'white', fontSize: '0.75rem', fontWeight: 700, cursor: uploading ? 'not-allowed' : 'pointer', width: 'auto', fontFamily: 'inherit', boxSizing: 'border-box' }}>
              {uploading ? 'Analysing…' : 'Analyse & pre-fill →'}
            </button>
          )}
        </div>
        {files.length > 0 && (
          <div style={{ marginTop: '0.5rem', display: 'flex', flexWrap: 'wrap', gap: '0.35rem' }}>
            {files.map((f, i) => (
              <span key={i} style={{ fontSize: '0.68rem', background: '#FFF3E1', color: '#7B4A0F', padding: '2px 6px 2px 8px', borderRadius: 20, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 3 }}>
                {f.name}
                <button onClick={() => removeFile(f.name)} style={{ background: 'none', border: 'none', color: '#7B4A0F', cursor: 'pointer', padding: 0, fontSize: '0.75rem', lineHeight: 1, width: 'auto', opacity: 0.7 }}>×</button>
              </span>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Main component ───────────────────────────────────────────────
export default function DeliveryGuru() {
  const navigate = useNavigate();
  const user = (() => { try { return JSON.parse(localStorage.getItem('guruone_user')); } catch { return null; } })();

  const [strategyData, setStrategyData] = useState({});
  const [designData, setDesignData] = useState({});
  const [savedInitiatives, setSavedInitiatives] = useState([]);
  const [loadingInitiatives, setLoadingInitiatives] = useState(true);

  const [view, setView] = useState('dashboard');
  const [showAddPanel, setShowAddPanel] = useState(false);
  const [showReviewPanel, setShowReviewPanel] = useState(false);
  const [showUploadPanel, setShowUploadPanel] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);
  // Drafts the user has explicitly dismissed (per-user, persisted across visits).
  const [dismissedDrafts, setDismissedDrafts] = useState(() => {
    try {
      const raw = localStorage.getItem('guruone_dismissed_drafts');
      const parsed = raw ? JSON.parse(raw) : [];
      return Array.isArray(parsed) ? parsed : [];
    } catch { return []; }
  });
  // SMART ratings keyed by initiative id, with the title/description we
  // last rated against so we can detect edits that need a re-rate.
  const [smartRatings, setSmartRatings] = useState({});
  // Cheap re-read on every render — readReviewCycle is a single
  // localStorage call. Keeps the wheels in sync with whatever the
  // panel just saved without needing to lift state.
  const reviewCycle = readReviewCycle();
  const [readiness, setReadiness] = useState(null);
  const [generatedInitiatives, setGeneratedInitiatives] = useState([]);
  const [summary, setSummary] = useState('');
  const [error, setError] = useState('');
  const [objectivesState, setObjectivesState] = useState({});
  const [refineModal, setRefineModal] = useState(null);

  const [bannerDismissed, setBannerDismissed] = useState(() => {
    try { return localStorage.getItem(BANNER_DISMISS_KEY) === 'true'; } catch { return false; }
  });
  const [orgRolesNoticeDismissed, setOrgRolesNoticeDismissed] = useState(false);
  const [expandedCategories, setExpandedCategories] = useState(() => new Set());

  const toggleCategory = useCallback((catKey) => {
    setExpandedCategories(prev => {
      const next = new Set(prev);
      if (next.has(catKey)) next.delete(catKey);
      else next.add(catKey);
      return next;
    });
  }, []);

  useEffect(() => {
    const userId = user?.id;
    if (!userId) return;
    try {
      const sRaw = localStorage.getItem('guruone_strategy');
      const sStore = sRaw ? JSON.parse(sRaw) : {};
      setStrategyData(sStore[`user_${userId}`] || {});
      const bRaw = localStorage.getItem('guruone_operating_model');
      const bStore = bRaw ? JSON.parse(bRaw) : {};
      setDesignData(bStore[`user_${userId}`] || {});
    } catch (e) {
      // localStorage error — proceed with empty
    }
  }, [user?.id]);

  const loadInitiatives = useCallback(async () => {
    if (!user?.id) { setLoadingInitiatives(false); return; }
    try {
      const res = await axios.get(`${DELIVERY_API}/initiatives/${user.id}`);
      setSavedInitiatives(res.data.initiatives || []);
    } catch (e) {
      // non-fatal
    }
    setLoadingInitiatives(false);
  }, [user?.id]);

  useEffect(() => { loadInitiatives(); }, [loadInitiatives]);

  const orgRoles = useMemo(() => extractOrgRoles(designData), [designData]);

  // "More org roles available" hint — show when the live org chart has more
  // roles than the user has previously seen here, and they have initiatives
  // that could be re-aligned. Soft notice, dismissable for this session.
  const orgRolesSeen = (() => {
    try { return parseInt(localStorage.getItem(ORGROLES_SEEN_KEY) || '0', 10) || 0; } catch { return 0; }
  })();
  const showOrgRolesNotice = !orgRolesNoticeDismissed
    && savedInitiatives.length > 0
    && orgRoles.length > orgRolesSeen
    && orgRolesSeen > 0; // only after a baseline is recorded

  // Establish the org-roles baseline the first time we have any initiatives
  useEffect(() => {
    if (!savedInitiatives.length) return;
    try {
      const seen = parseInt(localStorage.getItem(ORGROLES_SEEN_KEY) || '0', 10) || 0;
      if (seen === 0 && orgRoles.length > 0) {
        localStorage.setItem(ORGROLES_SEEN_KEY, String(orgRoles.length));
      }
    } catch { /* ignore */ }
  }, [savedInitiatives.length, orgRoles.length]);

  const directionPct = calcCanvasPct(strategyData, DIRECTION_CHECKS);
  const designPct = calcCanvasPct(designData, DESIGN_CHECKS);
  const showStarterBanner = !bannerDismissed
    && savedInitiatives.length === 0
    && (directionPct < 50 || designPct < 50);

  const dismissStarterBanner = () => {
    try { localStorage.setItem(BANNER_DISMISS_KEY, 'true'); } catch { /* ignore */ }
    setBannerDismissed(true);
  };

  const acknowledgeOrgRoles = () => {
    try { localStorage.setItem(ORGROLES_SEEN_KEY, String(orgRoles.length)); } catch { /* ignore */ }
    setOrgRolesNoticeDismissed(true);
  };

  const handleUpdateInitiative = useCallback(async (initiativeId, fields) => {
    if (!initiativeId || !user?.id) return;
    // Optimistic local update
    setSavedInitiatives(prev => prev.map(i => i.id === initiativeId ? { ...i, ...fields } : i));
    try {
      await axios.patch(`${DELIVERY_API}/initiatives/${initiativeId}`, { userId: user.id, fields });
      if (fields && Object.prototype.hasOwnProperty.call(fields, 'status')) {
        track('delivery_status_changed', { status: fields.status });
      }
    } catch (e) {
      // Revert on failure by reloading from server
      loadInitiatives();
    }
  }, [user?.id, loadInitiatives]);

  const handleDismissDraft = useCallback((draftId) => {
    setDismissedDrafts(prev => {
      if (prev.includes(draftId)) return prev;
      const next = [...prev, draftId];
      try { localStorage.setItem('guruone_dismissed_drafts', JSON.stringify(next)); } catch { /* ignore */ }
      return next;
    });
  }, []);

  // Commit flow: save the initiative, fire the SMART rating, dismiss
  // the draft from the suggestion list. Rating is non-fatal — if it
  // fails the initiative still saves and the user can re-rate later.
  const handleCommitDraft = useCallback(async (draft) => {
    if (!user?.id) return;
    const initiative = {
      title: draft.title,
      description: draft.description,
      category: draft.category,
      source: 'draft_committed',
      phase: 2,
      status: 'on_track',
      owner_role: draft.owner,
      target_date: draft.dueDate,
      priority_score: 50,
    };
    try {
      const res = await axios.post(`${DELIVERY_API}/initiatives`, { userId: user.id, initiative });
      const id = res?.data?.id;
      setSavedInitiatives(prev => [...prev, { ...initiative, id, last_status_update: new Date().toISOString() }]);

      if (id) {
        try {
          const ratingRes = await axios.post(`${DELIVERY_API}/objectives/${id}/smart-rate`, {
            title: draft.title,
            description: draft.description,
            owner: draft.owner,
            startDate: draft.startDate,
            dueDate: draft.dueDate,
          });
          const { success: _success, ...rating } = ratingRes.data || {};
          setSmartRatings(prev => ({
            ...prev,
            [id]: { rating, ratedFor: { title: draft.title, description: draft.description } },
          }));
        } catch (e) {
          // Rating failed — initiative still saved, user can re-rate.
        }
      }

      // Dismiss the draft so it disappears from the suggestion list.
      handleDismissDraft(draft.draftId);
    } catch (e) {
      // Non-fatal — user can retry the confirm.
    }
  }, [user?.id, handleDismissDraft]);

  const handleReRate = useCallback(async (initiative) => {
    if (!initiative?.id) return;
    try {
      const ratingRes = await axios.post(`${DELIVERY_API}/objectives/${initiative.id}/smart-rate`, {
        title: initiative.title,
        description: initiative.description,
        owner: initiative.owner_role,
        startDate: '',
        dueDate: initiative.target_date,
      });
      const { success: _success, ...rating } = ratingRes.data || {};
      setSmartRatings(prev => ({
        ...prev,
        [initiative.id]: { rating, ratedFor: { title: initiative.title, description: initiative.description } },
      }));
    } catch (e) {
      // non-fatal
    }
  }, []);

  const handleDoor1 = () => {
    const r = assessReadiness(strategyData, designData);
    setReadiness(r);
    if (r.gaps.length === 0 || r.filledCount >= 5) {
      runGenerate(r);
    } else {
      setView('door1_gaps');
    }
  };

  const runGenerate = async (r) => {
    const ready = r || readiness;
    setView('door1_generating');
    setError('');
    try {
      const res = await axios.post(`${DELIVERY_API}/generate`, { userId: user?.id, strategyData, designData });
      setGeneratedInitiatives(res.data.initiatives || []);
      setSummary(res.data.summary || '');
      setObjectivesState({});
      setView('door1_initiatives');
    } catch (e) {
      setError('Something went wrong generating initiatives. Please try again.');
      setView(ready?.filledCount >= 3 ? 'door1_gaps' : 'dashboard');
    }
  };

  const handleGenerateObjectives = async (initiativeIndex, initiative) => {
    setObjectivesState(prev => ({ ...prev, [initiativeIndex]: { generating: true } }));
    try {
      const res = await axios.post(`${DELIVERY_API}/initiatives/0/objectives/generate`, { initiative, orgRoles });
      const objs = res.data.objectives || [];
      setObjectivesState(prev => ({ ...prev, [initiativeIndex]: { generating: false, objectives: objs, accepted: new Array(objs.length).fill(false), saved: false } }));
    } catch (e) {
      setObjectivesState(prev => ({ ...prev, [initiativeIndex]: { generating: false, error: true } }));
    }
  };

  const handleAcceptObjective = async (initiativeIndex, objIndex) => {
    setObjectivesState(prev => {
      const s = { ...prev[initiativeIndex] };
      const acc = [...s.accepted];
      acc[objIndex] = true;
      return { ...prev, [initiativeIndex]: { ...s, accepted: acc, saved: acc.every(Boolean) } };
    });
    try {
      await axios.post(`${DELIVERY_API}/initiatives`, { userId: user?.id, initiative: { ...generatedInitiatives[initiativeIndex], source: 'canvas_generated' } });
    } catch (e) {
      // non-fatal
    }
  };

  const handleStartRefine = (initiativeIndex, objIndex) => {
    const obj = objectivesState[initiativeIndex]?.objectives?.[objIndex];
    if (obj) setRefineModal({ initiativeIndex, objIndex, objective: obj });
  };

  const handleAcceptRefined = (initiativeIndex, objIndex, refinedObj) => {
    setObjectivesState(prev => {
      const s = { ...prev[initiativeIndex] };
      const objectives = [...s.objectives];
      objectives[objIndex] = refinedObj;
      const acc = [...s.accepted];
      acc[objIndex] = true;
      return { ...prev, [initiativeIndex]: { ...s, objectives, accepted: acc, saved: acc.every(Boolean) } };
    });
    setRefineModal(null);
  };

  const allSaved = generatedInitiatives.length > 0 && generatedInitiatives.every((_, i) => objectivesState[i]?.saved);

  const handleInitiativeSaved = (initiative) => {
    setSavedInitiatives(prev => [...prev, initiative]);
    setShowAddPanel(false);
  };

  const handleBackToDashboard = () => navigate('/dashboard');

  const handleDownloadPlan = () => {
    alert('Coming soon — one-page delivery plan download is in development.');
  };

  // Setting this flag tells DeliveryJourneySidebar to show on the canvas
  // pages so the user can navigate back here once the gap is filled.
  // returnPath is /delivery (the post-rename Delivery Guru route).
  const handleGoThereForGap = (item) => {
    try {
      localStorage.setItem('guruone_delivery_journey', JSON.stringify({ active: true, returnPath: '/delivery' }));
    } catch { /* non-fatal */ }
    navigate(item.route);
  };

  return (
    <div style={{ minHeight: '100vh', background: '#f4f4f7' }}>
      <Header />

      {showAddPanel && (
        <AddInitiativePanel onClose={() => setShowAddPanel(false)} onSaved={handleInitiativeSaved} orgRoles={orgRoles} />
      )}

      {showReviewPanel && (
        <ReviewCyclePanel onClose={() => setShowReviewPanel(false)} />
      )}

      {helpOpen && <HowToUseDeliveryPanel onClose={() => setHelpOpen(false)} />}

      {showUploadPanel && (
        <div style={{
          position: 'fixed', inset: 0,
          background: 'rgba(0,0,0,0.4)',
          zIndex: 200,
          display: 'flex', alignItems: 'flex-start', justifyContent: 'flex-end',
        }}>
          <div style={{
            width: '100%', maxWidth: 420, height: '100vh',
            background: 'white',
            boxShadow: '-4px 0 24px rgba(0,0,0,0.12)',
            display: 'flex', flexDirection: 'column', overflow: 'hidden',
          }}>
            <div style={{
              padding: '1.1rem 1.4rem 0.9rem',
              borderBottom: '1px solid #f0f0f0',
              display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between',
              flexShrink: 0,
            }}>
              <div>
                <div style={{ fontSize: '0.62rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: '#aaa' }}>
                  Delivery Guru
                </div>
                <div style={{ fontSize: '1.05rem', fontWeight: 700, color: '#1a1a2e', marginTop: 2 }}>
                  Import existing plans
                </div>
                <div style={{ fontSize: '0.78rem', color: '#666', marginTop: '0.35rem', lineHeight: 1.5 }}>
                  Upload your business plan, project plans or board reports and we&apos;ll extract initiatives for you to review before saving.
                </div>
              </div>
              <button
                onClick={() => setShowUploadPanel(false)}
                aria-label="Close"
                style={{
                  background: 'none', border: 'none', cursor: 'pointer',
                  fontSize: '1.05rem', color: '#aaa', padding: '0.2rem 0.4rem',
                  lineHeight: 1, fontFamily: 'inherit',
                }}
              >
                ✕
              </button>
            </div>
            <div style={{ flex: 1, overflowY: 'auto', padding: '1.1rem 1.4rem' }}>
              <DeliveryGlobalUploadPanel />
            </div>
          </div>
        </div>
      )}

      {refineModal && (
        <RefineDialogue
          objective={refineModal.objective}
          initiativeIndex={refineModal.initiativeIndex}
          objIndex={refineModal.objIndex}
          onClose={() => setRefineModal(null)}
          onAcceptRefined={handleAcceptRefined}
        />
      )}

      <main style={{ maxWidth: 1300, margin: '0 auto', padding: '1.5rem' }}>

        {/* Dashboard */}
        {view === 'dashboard' && (
          <>
            {/* Subtle "← Dashboard" chevron link top-left */}
            <button
              onClick={handleBackToDashboard}
              style={{ background: 'none', border: 'none', color: '#888', fontSize: '0.72rem', cursor: 'pointer', padding: 0, fontFamily: 'inherit', marginBottom: '0.75rem' }}
            >
              ← Dashboard
            </button>

            {/* Journey indicator — sits above the masthead per spec, mirroring the other gurus */}
            <DeliveryJourneyTrack
              strategyData={strategyData}
              designData={designData}
              savedInitiatives={savedInitiatives}
              onOpenReview={() => setShowReviewPanel(true)}
            />

            {/* Header row — guru name centred in left flex region, review wheel pinned right */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              marginBottom: '1rem',
              gap: '1rem',
            }}>
              <div style={{ flex: 1, textAlign: 'center' }}>
                <h1 style={{ fontSize: '2.5rem', fontWeight: 300, color: '#1a1a2e', letterSpacing: '-0.02em', lineHeight: 1, margin: 0 }}>
                  Delivery
                  <strong style={{ color: '#D97706', fontWeight: 700 }}> Guru</strong>
                </h1>
                <div style={{ fontSize: '0.8rem', color: '#aaa', marginTop: '0.4rem', letterSpacing: '0.04em' }}>
                  {DELIVERY_TAGLINE}
                </div>
              </div>
              <div style={{ flexShrink: 0 }}>
                <ConcentricReviewWheels
                  cycle={reviewCycle}
                  size="xs"
                  onClick={() => setShowReviewPanel(true)}
                />
              </div>
            </div>

            {/* Dark guru toolbar — left group (logo + How to use + canvas label), right group (Add initiative / Generate plan / Download) */}
            {(() => {
              const toolbarBtn = {
                fontSize: '0.74rem', padding: '0.4rem 0.85rem', borderRadius: 18,
                border: '1px solid rgba(255,255,255,0.25)', background: 'transparent',
                color: 'white', cursor: 'pointer', fontWeight: 600, fontFamily: 'inherit',
                width: 'auto', whiteSpace: 'nowrap',
              };
              return (
                <div style={{
                  background: '#1a1a2e', color: 'white', borderRadius: 10,
                  padding: '0.55rem 0.85rem', marginBottom: '1.5rem',
                  display: 'flex', alignItems: 'center', gap: '0.75rem',
                }}>
                  {/* Left group */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.65rem', flex: 1 }}>
                    <div style={{ width: 28, height: 28, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      <img src="/Logo_emblem_only.png" alt="GuruOne" style={{ height: 22, width: 22, objectFit: 'contain', filter: 'brightness(0) invert(1)' }} />
                    </div>
                    <button onClick={() => setHelpOpen(true)} aria-label="How to use Delivery Guru" style={toolbarBtn}>
                      How to use
                    </button>
                    <span style={{ fontSize: '0.82rem', fontWeight: 700, color: 'white', whiteSpace: 'nowrap' }}>
                      Your Delivery Canvas
                    </span>
                  </div>
                  {/* Right group */}
                  <div style={{ display: 'flex', gap: '0.5rem', flexShrink: 0 }}>
                    <button
                      type="button"
                      onClick={() => setShowUploadPanel(true)}
                      style={toolbarBtn}
                    >
                      ✨ Import plans
                    </button>
                    <button type="button" onClick={() => setShowAddPanel(true)} style={toolbarBtn}>
                      + Add initiative
                    </button>
                    <button
                      type="button"
                      onClick={handleDoor1}
                      style={{
                        ...toolbarBtn,
                        background: GURU_COLOUR,
                        border: `1px solid ${GURU_COLOUR}`,
                        fontSize: '0.78rem',
                        fontWeight: 700,
                      }}
                    >
                      ✨ Generate delivery plan →
                    </button>
                    <button
                      onClick={handleDownloadPlan}
                      style={{ ...toolbarBtn, borderColor: 'rgba(167,227,167,0.55)', color: '#A7E3A7' }}
                    >
                      ⬇ Download 1-pager
                    </button>
                  </div>
                </div>
              );
            })()}

            {/* Suggested-starter banner (shown only when no inits and a canvas under 50%) */}
            {showStarterBanner && (
              <div style={{ background: '#FFFBEB', borderLeft: `4px solid ${GURU_COLOUR}`, borderRadius: 10, padding: '0.9rem 2.4rem 0.9rem 1.1rem', marginBottom: '1.25rem', position: 'relative' }}>
                <div style={{ fontSize: '0.85rem', fontWeight: 700, color: '#7B4A0F', marginBottom: '0.55rem' }}>
                  No initiatives yet — this is a suggested framework to get you started.
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.6rem 1.25rem', alignItems: 'center' }}>
                  <button onClick={() => setShowAddPanel(true)} style={{ background: 'none', border: 'none', color: GURU_COLOUR, fontSize: '0.78rem', fontWeight: 600, cursor: 'pointer', padding: 0, fontFamily: 'inherit', whiteSpace: 'nowrap' }}>
                    Start adding your own initiatives →
                  </button>
                  <button onClick={() => navigate('/strategy')} style={{ background: 'none', border: 'none', color: GURU_COLOUR, fontSize: '0.78rem', fontWeight: 600, cursor: 'pointer', padding: 0, fontFamily: 'inherit', whiteSpace: 'nowrap' }}>
                    Build your Direction first →
                  </button>
                  <button onClick={() => navigate('/operating-model')} style={{ background: 'none', border: 'none', color: GURU_COLOUR, fontSize: '0.78rem', fontWeight: 600, cursor: 'pointer', padding: 0, fontFamily: 'inherit', whiteSpace: 'nowrap' }}>
                    Build your Design first →
                  </button>
                </div>
                <button onClick={dismissStarterBanner} aria-label="Dismiss" style={{ position: 'absolute', top: 8, right: 10, background: 'none', border: 'none', cursor: 'pointer', fontSize: '1.05rem', color: '#bbb', padding: '0.2rem 0.4rem', lineHeight: 1, fontFamily: 'inherit' }}>×</button>
              </div>
            )}

            {/* Org-roles update notice */}
            {showOrgRolesNotice && (
              <div style={{ background: '#F0F9FF', border: '1px solid #BAE6FD', borderRadius: 10, padding: '0.75rem 2.4rem 0.75rem 1rem', marginBottom: '1rem', position: 'relative', display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
                <span style={{ fontSize: '0.95rem' }}>👥</span>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: '0.78rem', color: '#075985', lineHeight: 1.45 }}>
                    <strong>{orgRoles.length - orgRolesSeen}</strong> new role{(orgRoles.length - orgRolesSeen) === 1 ? '' : 's'} added in your Design Guru since you last reviewed owners. Want to align your initiatives?
                  </div>
                </div>
                <button onClick={acknowledgeOrgRoles} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '0.72rem', color: '#075985', fontWeight: 600, padding: '0.3rem 0.6rem', fontFamily: 'inherit' }}>Got it</button>
                <button onClick={acknowledgeOrgRoles} aria-label="Dismiss" style={{ position: 'absolute', top: 6, right: 8, background: 'none', border: 'none', cursor: 'pointer', fontSize: '0.95rem', color: '#7dd3fc', padding: '0.15rem 0.35rem', lineHeight: 1, fontFamily: 'inherit' }}>×</button>
              </div>
            )}

            {loadingInitiatives ? (
              <div style={{ textAlign: 'center', padding: '3rem 0', color: '#aaa', fontSize: '0.85rem' }}>Loading your initiatives…</div>
            ) : (
              <>
                <DashboardOverview initiatives={savedInitiatives} />
                <div style={{ fontSize: '0.7rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: '#aaa', marginBottom: '0.75rem' }}>
                  Delivery priorities — expand each area to see steps and initiatives
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: '0.75rem', alignItems: 'start' }}>
                  {CATEGORY_KEYS.map(k => (
                    <CategoryCard
                      key={k}
                      catKey={k}
                      initiatives={savedInitiatives}
                      orgRoles={orgRoles}
                      onUpdateInitiative={handleUpdateInitiative}
                      smartRatings={smartRatings}
                      onCommitDraft={handleCommitDraft}
                      onDismissDraft={handleDismissDraft}
                      onReRate={handleReRate}
                      dismissedDrafts={dismissedDrafts}
                      expanded={expandedCategories.has(k)}
                      onToggle={() => toggleCategory(k)}
                    />
                  ))}
                </div>

                {savedInitiatives.length === 0 && (
                  <div style={{ background: 'white', borderRadius: 12, padding: '2rem', textAlign: 'center', border: '1px dashed #e8e8f0', marginTop: '0.75rem' }}>
                    <div style={{ fontSize: '1.5rem', marginBottom: '0.75rem' }}>🧭</div>
                    <div style={{ fontSize: '0.88rem', fontWeight: 700, color: '#1a1a2e', marginBottom: '0.4rem' }}>No initiatives yet</div>
                    <p style={{ fontSize: '0.8rem', color: '#888', margin: 0, lineHeight: 1.6, maxWidth: 420, marginLeft: 'auto', marginRight: 'auto' }}>
                      Use <strong>+ Add initiative</strong> in the toolbar above to add one manually, or <strong>Generate delivery plan</strong> to draft one from your Direction and Design canvases.
                    </p>
                  </div>
                )}
              </>
            )}
          </>
        )}

        {/* Door 1: Gaps */}
        {view === 'door1_gaps' && readiness && (
          <>
            <button onClick={() => setView('dashboard')} style={{ background: 'none', border: 'none', color: GURU_COLOUR, fontSize: '0.82rem', cursor: 'pointer', padding: 0, fontFamily: 'inherit', marginBottom: '1.5rem' }}>← Back</button>
            <div style={{ textAlign: 'center', margin: '0 0 0.75rem' }}>
              <div style={{ fontSize: '2.5rem', fontWeight: 300, color: '#1a1a2e', letterSpacing: '-0.02em', lineHeight: 1 }}>
                Delivery
                <strong style={{ color: '#D97706', fontWeight: 700 }}> Guru</strong>
              </div>
              <div style={{ fontSize: '0.8rem', color: '#aaa', marginTop: '0.4rem', letterSpacing: '0.04em' }}>{DELIVERY_TAGLINE}</div>
            </div>
            <div style={{ background: 'white', borderRadius: 14, padding: '1.5rem', marginBottom: '1rem', border: '1px solid #e8e8f0' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.75rem' }}>
                <div style={{ fontSize: '0.88rem', fontWeight: 700, color: '#1a1a2e' }}>Canvas readiness</div>
                <ReadinessBar score={readiness.score} />
              </div>
              <p style={{ fontSize: '0.85rem', color: '#444', margin: '0 0 1.25rem', lineHeight: 1.55 }}>
                {readiness.filledCount} of {readiness.totalCount} key sections are filled. I need to understand more about your direction and business design to help you identify the actions that will get there.
              </p>
              {[
                { title: 'Direction Guru', checks: readiness.directionChecks },
                { title: 'Design Guru',    checks: readiness.designChecks },
              ].map(group => (
                <div key={group.title} style={{ marginBottom: '1rem' }}>
                  <div style={{ fontSize: '0.7rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: '#aaa', marginBottom: '0.35rem' }}>{group.title}</div>
                  {group.checks.map((c, i) => <CheckRow key={i} item={c} onGoThere={handleGoThereForGap} />)}
                </div>
              ))}
            </div>
            {error && <div style={{ background: '#FEE2E2', border: '1px solid #FECACA', borderRadius: 8, padding: '0.65rem 0.9rem', fontSize: '0.82rem', color: '#991B1B', marginBottom: '1rem' }}>{error}</div>}
            <button onClick={() => runGenerate(readiness)}
              style={{ width: '100%', padding: '0.85rem', borderRadius: 10, background: GURU_COLOUR, color: 'white', border: 'none', fontSize: '0.88rem', fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>
              Generate plan with what I have →
            </button>
            <p style={{ fontSize: '0.72rem', color: '#aaa', textAlign: 'center', marginTop: '0.5rem' }}>
              You can fill in more sections and regenerate at any time.
            </p>
          </>
        )}

        {/* Door 1: Generating */}
        {view === 'door1_generating' && (
          <div style={{ textAlign: 'center', padding: '5rem 0' }}>
            <div style={{ fontSize: '2rem', marginBottom: '1rem' }}>🧭</div>
            <div style={{ fontSize: '1rem', fontWeight: 700, color: '#1a1a2e', marginBottom: '0.5rem' }}>Building your delivery plan</div>
            <p style={{ fontSize: '0.82rem', color: '#888' }}>GuruOne is reading your canvas, analysing priorities, and structuring your initiatives…</p>
          </div>
        )}

        {/* Door 1: Initiatives */}
        {view === 'door1_initiatives' && (
          <>
            <button onClick={() => setView('dashboard')} style={{ background: 'none', border: 'none', color: GURU_COLOUR, fontSize: '0.82rem', cursor: 'pointer', padding: 0, fontFamily: 'inherit', marginBottom: '1.5rem' }}>← Back to dashboard</button>
            <div style={{ textAlign: 'center', margin: '0 0 0.75rem' }}>
              <div style={{ fontSize: '2.5rem', fontWeight: 300, color: '#1a1a2e', letterSpacing: '-0.02em', lineHeight: 1 }}>
                Delivery
                <strong style={{ color: '#D97706', fontWeight: 700 }}> Guru</strong>
              </div>
              <div style={{ fontSize: '0.8rem', color: '#aaa', marginTop: '0.4rem', letterSpacing: '0.04em' }}>{DELIVERY_TAGLINE}</div>
            </div>

            {summary && (
              <div style={{ background: 'white', borderRadius: 12, padding: '1.1rem 1.25rem', marginBottom: '1.25rem', border: `1.5px solid ${GURU_COLOUR}33`, borderLeft: `4px solid ${GURU_COLOUR}` }}>
                <div style={{ fontSize: '0.7rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: GURU_COLOUR, marginBottom: '0.35rem' }}>Prioritisation rationale</div>
                <p style={{ fontSize: '0.82rem', color: '#444', lineHeight: 1.6, margin: 0 }}>{summary}</p>
              </div>
            )}

            <div style={{ fontSize: '0.7rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: '#aaa', marginBottom: '0.75rem' }}>
              {generatedInitiatives.length} initiative{generatedInitiatives.length !== 1 ? 's' : ''} — generate SMART objectives on each to build out your plan
            </div>

            {generatedInitiatives.map((initiative, i) => {
              const state = objectivesState[i] || {};
              return (
                <div key={i}>
                  <InitiativeCard initiative={initiative} index={i} onGenerateObjectives={handleGenerateObjectives} objectivesState={objectivesState} />
                  {state.objectives && !state.saved && (
                    <div style={{ marginTop: '-0.5rem', marginBottom: '0.85rem', paddingLeft: '1rem', borderLeft: `2px solid ${GURU_COLOUR}44` }}>
                      <div style={{ fontSize: '0.7rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: '#aaa', margin: '0.6rem 0 0.75rem' }}>SMART objectives — accept or refine each one</div>
                      {state.objectives.map((obj, j) => (
                        <ObjectiveCard key={j} obj={obj} objIndex={j} initiativeIndex={i} onAccept={handleAcceptObjective} onStartRefine={handleStartRefine} accepted={state.accepted?.[j] || false} />
                      ))}
                    </div>
                  )}
                  {state.saved && state.objectives && (
                    <div style={{ marginTop: '-0.5rem', marginBottom: '0.85rem', paddingLeft: '1rem', borderLeft: '2px solid #1D9E75' }}>
                      <div style={{ fontSize: '0.7rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: '#aaa', margin: '0.6rem 0 0.75rem' }}>SMART objectives</div>
                      {state.objectives.map((obj, j) => (
                        <ObjectiveCard key={j} obj={obj} objIndex={j} initiativeIndex={i} onAccept={() => {}} onStartRefine={() => {}} accepted={true} />
                      ))}
                    </div>
                  )}
                  {state.error && <div style={{ fontSize: '0.78rem', color: '#DC2626', marginBottom: '0.85rem', paddingLeft: '1rem' }}>Something went wrong generating objectives. Please try again.</div>}
                </div>
              );
            })}

            {allSaved && (
              <div style={{ background: '#f0fdf8', border: '1px solid #a7f3d0', borderRadius: 12, padding: '1.5rem', textAlign: 'center', marginTop: '1rem' }}>
                <div style={{ fontSize: '1.5rem', marginBottom: '0.5rem' }}>✓</div>
                <div style={{ fontSize: '0.92rem', fontWeight: 700, color: '#065f46', marginBottom: '0.4rem' }}>Plan saved</div>
                <p style={{ fontSize: '0.8rem', color: '#6b7280', margin: '0 0 1rem' }}>All initiatives and SMART objectives are saved.</p>
                <button onClick={() => { loadInitiatives(); setView('dashboard'); }}
                  style={{ padding: '0.65rem 1.5rem', borderRadius: 8, background: '#1D9E75', color: 'white', border: 'none', fontSize: '0.85rem', fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}>
                  Back to delivery dashboard →
                </button>
              </div>
            )}
          </>
        )}

      </main>
    </div>
  );
}
